import { DocButton, StorageIndicator, deleteRecordThenDocuments, describeDeleteResult } from './DocumentPanel'
import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { useAuth } from '../context/AuthContext'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import { computeContractFinancials } from '../lib/contractFinancials'
import { buildSubcontractReportRows, buildIpcStatementRows } from '../lib/subcontractReportData'
import { renderFinancialStatementPdf } from '../lib/financialStatementPdfRenderer'
import { getRequiredApprover } from '../lib/approvalLimits'
import * as XLSX from 'xlsx'
import SubcontractReportControls from './SubcontractReportControls'
import ContractDocument from './ContractDocument'

async function logAudit(entityId, entityType, action, actorName, comment, prevStatus, newStatus, projectId) {
  try {
    await supabase.from('audit_trail').insert({
      entity_id: entityId, entity_type: entityType, action,
      actor_name: actorName, comment, previous_status: prevStatus,
      new_status: newStatus, project_id: projectId,
    })
  } catch(e) { console.error('audit log failed', e) }
}

const STATUS_COLORS = {
  draft:      'bg-gray-100 text-[#2E2C29]',
  approved:   'bg-[#DDD9D0] text-[#1A7A4A]',
  closed:     'bg-green-100 text-green-700',
  cancelled:  'bg-red-100 text-red-700',
}
const STATUSES = ['draft', 'approved', 'closed', 'cancelled']

// ── Approval workflow: Contract Manager -> Cost Control -> Director ──────
const APPROVAL_STAGES = {
  draft:        { label: 'Draft',                    bg: 'bg-gray-100',   text: 'text-gray-600' },
  cm_review:     { label: 'Pending Contract Manager',  bg: 'bg-blue-100',   text: 'text-blue-700' },
  cost_control: { label: 'Pending Cost Control',      bg: 'bg-amber-100',  text: 'text-amber-700' },
  director:     { label: 'Pending Director',          bg: 'bg-purple-100', text: 'text-purple-700' },
  approved:     { label: 'Approved',                  bg: 'bg-green-100',  text: 'text-green-700' },
  rejected:     { label: 'Rejected',                  bg: 'bg-red-100',    text: 'text-red-700' },
}
// Temp role selector (mirrors PurchaseRequisitions.jsx pattern until real auth exists)
const APPROVAL_ROLES = ['contract_manager', 'cost_control', 'director']

// Explicit per-column widths for the two main Subcontract tables,
// shared via <colgroup> to prevent the browser's default table layout
// from compressing columns to fit the viewport. Material has 15
// columns (includes "Type"), Equipment has 14 (no "Type").
const scMatColWidths = [44, 100, 120, 160, 100, 120, 180, 110, 90, 180, 130, 120, 110, 100, 90]
const scEqColWidths = [44, 100, 120, 160, 100, 120, 180, 110, 180, 130, 120, 110, 100, 90]
const APPROVAL_ROLE_LABELS = { contract_manager: 'Contract Manager', cost_control: 'Cost Control', director: 'Director' }

const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(n || 0)

export default function Subcontracts({ projectId: initProjectId, onNavigate }) {
  // Project's own primary currency -- needed to resolve each Contract's
  // historical exchange_rate_applied/project_currency_amount at save time.
  const [projectCurrency, setProjectCurrency] = useState(null)
  const [mainTab,          setMainTab]          = useState('material') // material | equipment | financial
  const [finSummary,       setFinSummary]       = useState([])
  const [finLoading,       setFinLoading]       = useState(false)
  const [finSelectedIds,   setFinSelectedIds]   = useState(new Set()) // Financial Statement print selection
  const [finPrinting,       setFinPrinting]      = useState(false)
  const [statementContract, setStatementContract] = useState(null) // contract row to show statement for
  const [statementRows,     setStatementRows]     = useState([])
  const [statementLoading,  setStatementLoading]  = useState(false)
  const [finSearch,         setFinSearch]          = useState('')
  const [projects,         setProjects]         = useState([])
  const [currencies,       setCurrencies]       = useState([])
  const [projectId,        setProjectId]        = useState(null)
  useEffect(() => { if (initProjectId) setProjectId(initProjectId) }, [initProjectId])
  const [projectName,      setProjectName]      = useState('')
  const [contracts,        setContracts]        = useState([])
  const [availablePOs,     setAvailablePOs]     = useState([])
  const [costCodes,        setCostCodes]        = useState([])
  const [subCodesList,     setSubCodesList]     = useState([]) // level=2 with resolved Cost Code + Resource Code ancestry
  const [loading,          setLoading]          = useState(false)
  const [showModal,        setShowModal]        = useState(false)
  const [showSubReport,    setShowSubReport]    = useState(false)
  const [editContract,     setEditContract]     = useState(null)
  const [selectedContract, setSelectedContract] = useState(null)
  const [poLines,          setPoLines]          = useState([])
  const [filterStatus,     setFilterStatus]     = useState('all')
  const [searchQuery,      setSearchQuery]      = useState('')
  const [filterLogistics,  setFilterLogistics]  = useState('all')
  const [stats,            setStats]            = useState({ total: 0, approved: 0, totalValue: 0, certifiedToDate: 0 })
  const [showUpload,       setShowUpload]       = useState(false)
  const [uploadRows,       setUploadRows]       = useState([])
  const [uploadError,      setUploadError]      = useState('')
  const fileRef = useRef()

  // ── BULK CONVERT STATE ─────────────────────────────────────
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [unconvertedPOs,   setUnconvertedPOs]   = useState([])
  const [convertRows,      setConvertRows]      = useState([])   // { po, contract_no, retention_pct, payment_terms, checked }
  const [converting,       setConverting]       = useState(false)
  const [convertResult,    setConvertResult]    = useState(null) // { success, failed }

  // ═══ Equipment Convert PO → Contract ═══
  const [eqShowConvertModal, setEqShowConvertModal] = useState(false)
  const [eqUnconvertedPOs,   setEqUnconvertedPOs]   = useState([])
  const [eqConvertRows,      setEqConvertRows]      = useState([])
  const [eqConverting,       setEqConverting]       = useState(false)
  const [eqConvertResult,    setEqConvertResult]    = useState(null)

  // ── BULK APPROVE STATE ─────────────────────────────────────
  const [selectedIds,      setSelectedIds]      = useState(new Set())
  const [bulkApproving,    setBulkApproving]    = useState(false)
  const [bulkActionType,   setBulkActionType]    = useState('approve')
  const [eqBulkActionType, setEqBulkActionType]  = useState('approve')

  // ── Real authorization source -- can() mirrors has_permission()
  // exactly, same hook pattern as IPC/PO/PR/EquipmentPR.
  const { can } = useProjectAuth(projectId)
  const { profile } = useAuth()
  const currentUserName = profile?.full_name || profile?.email || 'Unknown User'

  // Material/Equipment Contracts table scroll/floating-nav state --
  // same proven pattern as PurchaseOrders.jsx: table+thead+tbody all
  // live in ONE table inside ONE overflow:auto/maxHeight container
  // (no separate header table, no clip-path/transform), and the
  // floating nav is position:fixed with left/right recomputed from the
  // table card's own bounding box so it lines up with the table rather
  // than the full window (independent of the app sidebar's width).
  const scMatScrollRef = useRef(null)
  const scMatCardRef = useRef(null)
  const [scMatNavLeftRight, setScMatNavLeftRight] = useState({ left: 0, right: 0 })
  const [scMatScrollState, setScMatScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const updateScMatScrollState = useCallback(() => {
    const el = scMatScrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setScMatScrollState({ hasOverflow, atStart: el.scrollLeft <= 1, atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1, ratio })
    if (scMatCardRef.current) {
      const rect = scMatCardRef.current.getBoundingClientRect()
      setScMatNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
    }
  }, [])
  const scMatScrollBy = (dir) => { const el = scMatScrollRef.current; if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' }) }
  const scMatScrollToRatio = (ratio) => { const el = scMatScrollRef.current; if (!el) return; const maxScroll = el.scrollWidth - el.clientWidth; el.scrollLeft = ratio * maxScroll }

  const scEqScrollRef = useRef(null)
  const scEqCardRef = useRef(null)
  const [scEqNavLeftRight, setScEqNavLeftRight] = useState({ left: 0, right: 0 })
  const [scEqScrollState, setScEqScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const updateScEqScrollState = useCallback(() => {
    const el = scEqScrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setScEqScrollState({ hasOverflow, atStart: el.scrollLeft <= 1, atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1, ratio })
    if (scEqCardRef.current) {
      const rect = scEqCardRef.current.getBoundingClientRect()
      setScEqNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
    }
  }, [])
  const scEqScrollBy = (dir) => { const el = scEqScrollRef.current; if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' }) }
  const scEqScrollToRatio = (ratio) => { const el = scEqScrollRef.current; if (!el) return; const maxScroll = el.scrollWidth - el.clientWidth; el.scrollLeft = ratio * maxScroll }

  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [printContractId, setPrintContractId] = useState(null)

  // ── Addendum state ─────────────────────────────────────────
  // addendumTarget state removed — Addendum creation happens entirely
  // in PurchaseOrders.jsx now, this page no longer tracks a "target
  // contract for the Addendum form" since that form no longer exists.

  // ── Audit history (per contract, and its linked PO) ─────────
  const [auditHistory, setAuditHistory] = useState([])
  async function fetchAuditHistory(contractId, contractType, poId) {
    const contractEntityType = contractType === 'equipment' ? 'equipment_contract' : 'subcontract'
    const poEntityType = contractType === 'equipment' ? 'rental_equipment_po' : 'purchase_order'
    const ids = [contractId, poId].filter(Boolean)
    const types = [contractEntityType, poEntityType]
    const { data } = await supabase
      .from('audit_trail')
      .select('*')
      .in('entity_id', ids)
      .in('entity_type', types)
      .order('created_at', { ascending: false })
    setAuditHistory(data || [])
  }
  // Addendum creation UI REMOVED from this page entirely, per the
  // approved workflow revision. An Addendum is now created as a full
  // Purchase Order (PurchaseOrders.jsx's openAddendumPO()/
  // repOpenAddendumPO()), goes through the normal PO approval cycle,
  // and only upon full approval does PurchaseOrders.jsx write the
  // resulting subcontract_addendums record — this page never creates
  // that record directly anymore. This page is now read-only for
  // Addendums: it shows the permanent Addendum History (unchanged
  // AddendumHistorySection below) and, where an original PO is in
  // draft awaiting an Addendum, a link to Purchase Orders rather than
  // a form.


  // ── Equipment Contracts (separate table: equipment_contracts) ──────
  const [eqContracts,      setEqContracts]      = useState([])

  useEffect(() => {
    updateScMatScrollState()
    updateScEqScrollState()
    const roMat = scMatScrollRef.current ? new ResizeObserver(updateScMatScrollState) : null
    if (roMat && scMatScrollRef.current) { roMat.observe(scMatScrollRef.current); if (scMatCardRef.current) roMat.observe(scMatCardRef.current) }
    const roEq = scEqScrollRef.current ? new ResizeObserver(updateScEqScrollState) : null
    if (roEq && scEqScrollRef.current) { roEq.observe(scEqScrollRef.current); if (scEqCardRef.current) roEq.observe(scEqCardRef.current) }
    const onResize = () => { updateScMatScrollState(); updateScEqScrollState() }
    window.addEventListener('resize', onResize)
    return () => { roMat?.disconnect(); roEq?.disconnect(); window.removeEventListener('resize', onResize) }
  }, [contracts?.length, eqContracts?.length, updateScMatScrollState, updateScEqScrollState])

  const [eqAvailablePOs,   setEqAvailablePOs]   = useState([])
  const [eqCostCodes,      setEqCostCodes]      = useState([])
  const [eqSubCodesList,   setEqSubCodesList]   = useState([]) // level=2 with resolved ancestry, for Equipment Contracts
  const [eqLoading,        setEqLoading]        = useState(false)
  const [eqShowModal,      setEqShowModal]      = useState(false)
  const [eqEditContract,   setEqEditContract]   = useState(null)
  const [eqSelectedContract, setEqSelectedContract] = useState(null)
  const [eqPoLines,        setEqPoLines]        = useState([])
  const [eqSubCodeDescMap, setEqSubCodeDescMap]  = useState({})

  const scStage = editContract?.approval_stage || 'draft'
  const scIsLockedForNonCC = !!editContract && !can('Subcontract.ApproveCostControl') && !['draft','rejected'].includes(scStage)
  const scIsCostControlPartial = !!editContract && can('Subcontract.ApproveCostControl') && ['cost_control','director','approved'].includes(scStage)
  const scIsReadOnly = scIsLockedForNonCC
  // Adv Recovery Rate / VAT Case affect all IPCs live — lock them once any IPC exists (must Revoke/delete first to edit).
  const scFinancialFieldsLocked = !!editContract && !!editContract._hasIpc

  const eqScStage = eqEditContract?.approval_stage || 'draft'
  const eqScIsLockedForNonCC = !!eqEditContract && !can('Subcontract.ApproveCostControl') && !['draft','rejected'].includes(eqScStage)
  const eqScIsCostControlPartial = !!eqEditContract && can('Subcontract.ApproveCostControl') && ['cost_control','director','approved'].includes(eqScStage)
  const eqScIsReadOnly = eqScIsLockedForNonCC
  const eqScFinancialFieldsLocked = !!eqEditContract && !!eqEditContract._hasIpc
  const [eqFilterStatus,   setEqFilterStatus]   = useState('all')
  const [eqSearchQuery,    setEqSearchQuery]    = useState('')
  const [eqStats,          setEqStats]          = useState({ total: 0, approved: 0, totalValue: 0, certifiedToDate: 0 })
  const [eqSelectedIds,    setEqSelectedIds]    = useState(new Set())
  const [eqBulkApproving,  setEqBulkApproving]  = useState(false)
  const [eqRejectTarget,   setEqRejectTarget]   = useState(null)
  const [eqRejectReason,   setEqRejectReason]   = useState('')
  const [eqPrintContractId,setEqPrintContractId]= useState(null)

  async function eqHandleApprovalAction(contract, action) {
    if (action === 'approve') {
      let update = {}
      const prevStage = contract.approval_stage || 'draft'
      const requiredApprover = getRequiredApprover(parseFloat(contract.contract_value) || 0)
      if (contract.approval_stage === 'draft' || !contract.approval_stage) {
        update = { approval_stage: 'cost_control', cm_approved_by: currentUserName, cm_approved_at: new Date().toISOString() }
      } else if (contract.approval_stage === 'cost_control') {
        if (requiredApprover === 'cost_control') {
          // ≤ approval threshold → approved directly, same rule as
          // PurchaseOrders.jsx's PO approval (single shared source,
          // lib/approvalLimits.js) — Contract-level approval no longer
          // has its own separate, unlimited-value path to Director.
          update = { approval_stage: 'approved', status: 'approved', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
        } else {
          update = { approval_stage: 'director', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
        }
      } else if (contract.approval_stage === 'director') {
        update = { approval_stage: 'approved', status: 'approved', director_approved_by: currentUserName, director_approved_at: new Date().toISOString() }
      }
      const { error } = await supabase.from('equipment_contracts').update(update).eq('id', contract.id)
      if (error) { alert('خطأ: ' + error.message); return }
      await logAudit(contract.id, 'equipment_contract', prevStage === 'draft' ? 'submit' : 'approve', currentUserName, '', prevStage, update.approval_stage, projectId)
      await eqFetchContracts(projectId)
      if (eqSelectedContract?.id === contract.id) setEqSelectedContract(c => ({ ...c, ...update }))
    } else if (action === 'reject') {
      setEqRejectTarget(contract)
      setEqRejectReason('')
    } else if (action === 'resubmit') {
      const prevStage = contract.approval_stage
      const update = { approval_stage: 'draft', status: 'draft', rejection_reason: null }
      const { error } = await supabase.from('equipment_contracts').update(update).eq('id', contract.id)
      if (error) { alert('خطأ: ' + error.message); return }
      await logAudit(contract.id, 'equipment_contract', 'resubmit', currentUserName, '', prevStage, 'draft', projectId)
      await eqFetchContracts(projectId)
      if (eqSelectedContract?.id === contract.id) setEqSelectedContract(c => ({ ...c, ...update }))
    } else if (action === 'revoke') {
      const prevStage = contract.approval_stage
      const update = { approval_stage: 'draft', status: 'draft', cm_approved_by: null, cm_approved_at: null, cc_approved_by: null, cc_approved_at: null, director_approved_by: null, director_approved_at: null }
      const { error } = await supabase.from('equipment_contracts').update(update).eq('id', contract.id)
      if (error) { alert('خطأ: ' + error.message); return }
      await logAudit(contract.id, 'equipment_contract', 'revoke', currentUserName, '', prevStage, 'draft', projectId)

      // Also revoke the linked PO in the SAME action — per the approved
      // workflow ("Last PO/Contract approver must Revoke first → Original
      // PO becomes Draft"), this is meant to be ONE logical step, not two
      // separate manual actions the user has to remember to do in both
      // PurchaseOrders.jsx and here. Without this, the Contract could sit
      // in 'draft' while its linked PO is still 'approved' — the "+
      // Addendum" button in PurchaseOrders.jsx checks the PO's own stage,
      // so it would never appear, and the user would be stuck.
      if (contract.po_id) {
        const { data: po } = await supabase.from('rental_equipment_pos')
          .select('id, approval_stage').eq('id', contract.po_id).maybeSingle()
        if (po && po.approval_stage === 'approved') {
          const poPrevStage = po.approval_stage
          await supabase.from('rental_equipment_pos').update({
            approval_stage: 'draft', status: 'draft',
            cc_approved_by: null, cc_approved_at: null, dir_approved_by: null, dir_approved_at: null,
          }).eq('id', contract.po_id)
          await logAudit(contract.po_id, 'rental_equipment_po', 'revoke', currentUserName, 'Auto-revoked with linked contract', poPrevStage, 'draft', projectId)
        }
      }
      await eqFetchContracts(projectId)
      if (eqSelectedContract?.id === contract.id) setEqSelectedContract(c => ({ ...c, ...update }))
    }
  }

  async function eqConfirmReject() {
    if (!eqRejectTarget) return
    const prevStage = eqRejectTarget.approval_stage
    const { error } = await supabase.from('equipment_contracts')
      .update({ approval_stage: 'rejected', rejection_reason: eqRejectReason || null })
      .eq('id', eqRejectTarget.id)
    if (error) { alert('خطأ: ' + error.message); return }
    await logAudit(eqRejectTarget.id, 'equipment_contract', 'reject', currentUserName, eqRejectReason, prevStage, 'rejected', projectId)
    await eqFetchContracts(projectId)
    setEqRejectTarget(null)
    setEqRejectReason('')
  }

  const eqEmptyForm = () => ({
    po_id: '', contract_no: '', contractor_name: '', subcontractor_contact: '',
    subcontractor_email: '', scope: '', contract_value: '', vat_pct: 15,
    contract_date: '', retention_pct: 0, advance_pct: 0, adv_recovery_rate: 0, vat_case: 1, payment_terms: 30,
    currency: 'SAR', cost_code_id: '', sub_code: '', notes: '', status: 'draft',
  })
  const [eqForm, setEqForm] = useState(eqEmptyForm())

  async function handleApprovalAction(contract, action) {
    if (action === 'approve') {
      let update = {}
      const prevStage = contract.approval_stage || 'draft'
      const requiredApprover = getRequiredApprover(parseFloat(contract.contract_value) || 0)
      if (contract.approval_stage === 'draft' || !contract.approval_stage) {
        update = { approval_stage: 'cost_control', cm_approved_by: currentUserName, cm_approved_at: new Date().toISOString() }
      } else if (contract.approval_stage === 'cost_control') {
        if (requiredApprover === 'cost_control') {
          update = { approval_stage: 'approved', status: 'approved', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
        } else {
          update = { approval_stage: 'director', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
        }
      } else if (contract.approval_stage === 'director') {
        update = { approval_stage: 'approved', status: 'approved', director_approved_by: currentUserName, director_approved_at: new Date().toISOString() }
      }
      const { error } = await supabase.from('subcontracts').update(update).eq('id', contract.id)
      if (error) { alert('خطأ: ' + error.message); return }
      await logAudit(contract.id, 'subcontract', prevStage === 'draft' ? 'submit' : 'approve', currentUserName, '', prevStage, update.approval_stage, projectId)
      await fetchContracts(projectId)
      if (selectedContract?.id === contract.id) setSelectedContract(c => ({ ...c, ...update }))
    } else if (action === 'reject') {
      setRejectTarget(contract)
      setRejectReason('')
    } else if (action === 'resubmit') {
      const prevStage = contract.approval_stage
      const update = { approval_stage: 'draft', status: 'draft', rejection_reason: null }
      const { error } = await supabase.from('subcontracts').update(update).eq('id', contract.id)
      if (error) { alert('خطأ: ' + error.message); return }
      await logAudit(contract.id, 'subcontract', 'resubmit', currentUserName, '', prevStage, 'draft', projectId)
      await fetchContracts(projectId)
      if (selectedContract?.id === contract.id) setSelectedContract(c => ({ ...c, ...update }))
    } else if (action === 'revoke') {
      const prevStage = contract.approval_stage
      const update = { approval_stage: 'draft', status: 'draft', cm_approved_by: null, cm_approved_at: null, cc_approved_by: null, cc_approved_at: null, director_approved_by: null, director_approved_at: null }
      const { error } = await supabase.from('subcontracts').update(update).eq('id', contract.id)
      if (error) { alert('خطأ: ' + error.message); return }
      await logAudit(contract.id, 'subcontract', 'revoke', currentUserName, '', prevStage, 'draft', projectId)

      // Same reasoning as eqHandleApprovalAction's revoke branch above —
      // one logical Revoke action covers both the Contract and its
      // linked PO, so the "+ Addendum" button in PurchaseOrders.jsx
      // (which checks the PO's own approval_stage) becomes available
      // immediately, without a second manual step.
      if (contract.po_id) {
        const { data: po } = await supabase.from('purchase_orders')
          .select('id, approval_stage').eq('id', contract.po_id).maybeSingle()
        if (po && po.approval_stage === 'approved') {
          const poPrevStage = po.approval_stage
          await supabase.from('purchase_orders').update({
            approval_stage: 'draft', status: 'draft',
            cc_approved_by: null, cc_approved_at: null, dir_approved_by: null, dir_approved_at: null,
          }).eq('id', contract.po_id)
          await logAudit(contract.po_id, 'purchase_order', 'revoke', currentUserName, 'Auto-revoked with linked contract', poPrevStage, 'draft', projectId)
        }
      }
      await fetchContracts(projectId)
      if (selectedContract?.id === contract.id) setSelectedContract(c => ({ ...c, ...update }))
    }
  }

  async function confirmReject() {
    if (!rejectTarget) return
    const prevStage = rejectTarget.approval_stage
    const { error } = await supabase.from('subcontracts')
      .update({ approval_stage: 'rejected', rejection_reason: rejectReason || null })
      .eq('id', rejectTarget.id)
    if (error) { alert('خطأ: ' + error.message); return }
    await logAudit(rejectTarget.id, 'subcontract', 'reject', currentUserName, rejectReason, prevStage, 'rejected', projectId)
    await fetchContracts(projectId)
    setRejectTarget(null)
    setRejectReason('')
  }

  const emptyForm = () => ({
    po_id: '', contract_no: '', contractor_name: '', subcontractor_contact: '',
    subcontractor_email: '', scope: '', contract_value: '', vat_pct: '0',
    contract_date: '', retention_pct: '10', advance_pct: '0',
    adv_recovery_rate: '0', vat_case: 1,
    payment_terms: '30', status: 'draft', currency: 'SAR',
    cost_code_id: '', sub_code: '', notes: ''
  })
  const [form, setForm] = useState(emptyForm())
  const hf = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => { fetchProjects() }, [])
  useEffect(() => { fetchCurrencies() }, [])
  useEffect(() => {
    if (!projectId) { setProjectCurrency(null); return }
    supabase.from('projects').select('currency').eq('id', projectId).single()
      .then(({ data }) => setProjectCurrency(data?.currency || null))
  }, [projectId])
  useEffect(() => { if (projectId) fetchAll(projectId) }, [projectId])

  // ── DATA FETCHING ──────────────────────────────────────────
  async function fetchProjects() {
    const cached = getCached('projects_list')
    if (cached) { setProjects(cached); }
    else {
      const { data } = await supabase.from('projects').select('id, name, organizations(name)').order('name')
      setProjects(data || [])
      if (data) setCached('projects_list', data)
    }
  }

  // Currency master, replacing the two previously hardcoded dropdown
  // lists below -- only active currencies are offered.
  async function fetchCurrencies() {
    const { data } = await supabase.from('currencies').select('code,name').eq('is_active', true).order('code')
    setCurrencies(data || [])
  }

  async function fetchAll(pid) {
    setLoading(true)
    await Promise.all([fetchContracts(pid), fetchAvailablePOs(pid), fetchCostCodes()])
    setLoading(false)
  }

  async function fetchContracts(pid) {
    const id = pid || projectId
    if (!id) return
    const { data, error } = await supabase
      .from('subcontracts')
      .select('*, purchase_orders(po_number, total_exc_vat, total_inc_vat, vendor), cost_codes(code, description)')
      .eq('project_id', id)
      .order('created_at', { ascending: false })
    if (error) { console.error('fetchContracts:', error.message); return }
    const list = data || []
    // fetch certified amounts + IPC existence separately
    if (list.length > 0) {
      const { data: ipcs, error: ipcErr } = await supabase
        .from('ipc_certificates')
        .select('subcontract_id, gross_amount, status')
        .in('subcontract_id', list.map(x => x.id))
      if (ipcErr) console.error('fetchContracts ipc check:', ipcErr.message)
      const ipcMap = {}
      const hasIpcMap = {}
      ;(ipcs || []).forEach(i => {
        hasIpcMap[i.subcontract_id] = true
        if (i.status === 'approved') ipcMap[i.subcontract_id] = (ipcMap[i.subcontract_id] || 0) + (parseFloat(i.gross_amount) || 0)
      })
      list.forEach(c => { c._certified = ipcMap[c.id] || 0; c._hasIpc = !!hasIpcMap[c.id] })
    }
    setContracts(list)
    calcStats(list)
  }

  async function fetchAvailablePOs(pid) {
    const id = pid || projectId
    if (!id) return
    const { data } = await supabase
      .from('purchase_orders')
      .select('id, po_number, vendor, total_exc_vat, total_inc_vat, po_type, notes, vat_pct, currency')
      .eq('status', 'approved')
      .in('po_type', ['complex', 'equipment'])
      .eq('project_id', id)
      .order('po_number')
    setAvailablePOs(data || [])
  }

  async function fetchCostCodes() {
    const { data } = await supabase.from('cost_codes').select('id, code, description, unit, level, parent_id').eq('project_id', projectId).eq('is_active', true).order('code')
    const all = data || []
    setCostCodes(all)
    setSubCodesList(buildSubCodesList(all))
  }

  // Shared helper: given the full cost_codes hierarchy for a project, build the list of
  // Sub Codes (level 2) with their Cost Code + Resource Code ancestry resolved.
  function buildSubCodesList(all) {
    const byId = {}
    all.forEach(c => { byId[c.id] = c })
    return all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let costCode = null, resourceCode = null
      if (parent && parseInt(parent.level) === 4) {
        resourceCode = parent
        costCode = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        costCode = parent
      }
      return {
        ...sub,
        costCodeId: costCode?.id || null,
        costCode: costCode?.code || '—',
        costCodeDescription: costCode?.description || '',
        resourceCode: resourceCode?.code || null,
        resourceCodeDescription: resourceCode?.description || null,
      }
    })
  }

  // ═══ Equipment Contracts ═══
  useEffect(() => { if (projectId) eqFetchAll(projectId) }, [projectId])

  // ═══ Financial Summary tab ═══
  // #11: the Material tab's Retention Held card reads the SAME rows (computeContractFinancials) as the Financial tab, so they are loaded for both tabs
  // and reloaded whenever the contract list is reloaded (after create / delete / convert), so the card never goes stale.
  useEffect(() => { if (projectId && (mainTab === 'financial' || mainTab === 'material')) fetchFinancialSummary(projectId) }, [projectId, mainTab, contracts])

  async function fetchFinancialSummary(pid) {
    setFinLoading(true)
    try {
      const [
        { data: subs },
        { data: eqSubs },
        { data: ipcs },
        { data: payments },
        { data: releases },
      ] = await Promise.all([
        supabase.from('subcontracts').select('id, contract_no, contractor_name, contract_value, po_id').eq('project_id', pid),
        supabase.from('equipment_contracts').select('id, contract_no, contractor_name, contract_value, po_id').eq('project_id', pid),
        supabase.from('ipc_certificates').select('subcontract_id, equipment_contract_id, gross_amount, retention_amount, net_payable, status').eq('project_id', pid).in('status', ['approved', 'paid']),
        supabase.from('cf_payment_entries').select('po_id, amount_inc_vat, category').eq('project_id', pid),
        supabase.from('retention_releases').select('subcontract_id, equipment_contract_id, amount').eq('project_id', pid),
      ])

      // Stage 2 (Source of Truth unification): replaced local buildRow()
      // with shared computeContractFinancials(). Old logic archived
      // separately (see project cleanup notes) after Stage 2 approval.
      const contracts = [
        ...(subs || []).map(c => ({ ...c, contract_type: 'material' })),
        ...(eqSubs || []).map(c => ({ ...c, contract_type: 'equipment' })),
      ]
      const rows = computeContractFinancials(contracts, ipcs, releases, payments, { onlyWithActivity: true })

      setFinSummary(rows)
    } catch (e) { console.error('fetchFinancialSummary:', e.message) }
    setFinLoading(false)
  }

  /**
   * Financial Statement print — approved flow (unchanged from the audit):
   *   Financial Summary → user selects contractor(s) → Print →
   *   fetch metadata for ONLY the selected contracts (WHERE id IN) →
   *   compute financial values via the SAME computeContractFinancials() →
   *   buildSubcontractReportRows() for the Variation Chain (reused
   *   verbatim from subcontractReportData.js, unchanged) →
   *   financialStatementPdfRenderer → A4 print window.
   *
   * fetchFinancialSummary() above is completely untouched — this is a
   * separate, parallel data-preparation path that happens to reuse the
   * exact same SSOT function, per the approved constraint "keep that
   * pipeline unchanged unless a field is genuinely required by the
   * print statement" (contract_date/practical_completion_date/
   * dlp_months/status/addendums ARE genuinely required by the print
   * spec but were never part of the on-screen table, so they're fetched
   * here only, not added to fetchFinancialSummary's query).
   */
  async function handlePrintFinancialStatement() {
    if (finSelectedIds.size === 0) return
    setFinPrinting(true)
    try {
      const selectedIdArray = [...finSelectedIds]

      // Only the selected contracts' metadata — WHERE id IN, not all
      // contracts. select('*') here is scoped to a small, explicit
      // subset (never the whole project), so it does not repeat the
      // earlier "select('*') for the whole table" pattern this task was
      // asked to avoid — it is a targeted fetch for exactly the rows
      // the user chose to print.
      const [
        { data: subs },
        { data: eqSubs },
      ] = await Promise.all([
        supabase.from('subcontracts').select('*').in('id', selectedIdArray),
        supabase.from('equipment_contracts').select('*').in('id', selectedIdArray),
      ])
      const selectedContracts = [
        ...(subs || []).map(c => ({ ...c, contract_type: 'material' })),
        ...(eqSubs || []).map(c => ({ ...c, contract_type: 'equipment' })),
      ]
      if (selectedContracts.length === 0) { setFinPrinting(false); return }

      // Same IPC/payment/release fetch pattern as fetchFinancialSummary
      // above (project-scoped — computeContractFinancials() matches by
      // po_id internally, so these three do not need a separate
      // "selected contracts only" filter to produce correct values for
      // the selected subset).
      const [
        { data: ipcs },
        { data: payments },
        { data: releases },
        { data: subAddendums },
        { data: eqAddendums },
      ] = await Promise.all([
        supabase.from('ipc_certificates').select('subcontract_id, equipment_contract_id, gross_amount, retention_amount, net_payable, status').eq('project_id', projectId).in('status', ['approved', 'paid']),
        supabase.from('cf_payment_entries').select('po_id, amount_inc_vat, category').eq('project_id', projectId),
        supabase.from('retention_releases').select('subcontract_id, equipment_contract_id, amount').eq('project_id', projectId),
        supabase.from('subcontract_addendums').select('*').eq('contract_type', 'material'),
        supabase.from('subcontract_addendums').select('*').eq('contract_type', 'equipment'),
      ])
      const addendums = [...(subAddendums || []), ...(eqAddendums || [])]

      // SAME functions as the main Subcontract Report — computeContractFinancials()
      // is called internally by buildSubcontractReportRows(), unchanged,
      // no second calculation engine.
      const rows = buildSubcontractReportRows(selectedContracts, ipcs || [], releases || [], payments || [], addendums)

      // IPC-by-IPC data for the Detailed IPC Statement section — SAME
      // shared helper the on-screen Statement modal uses
      // (buildIpcStatementRows(), subcontractReportData.js). Called
      // once per selected contract, in parallel, keyed by contract id
      // so the renderer can never mix one contractor's IPCs into
      // another's section. po_id is read from selectedContracts
      // (already fetched via select('*') above) rather than relying on
      // buildIpcStatementRows()'s own fallback lookup every time — same
      // fallback still exists in that helper for callers that don't
      // have po_id handy, but is unnecessary here since we already do.
      const poIdByContractId = new Map(selectedContracts.map(c => [c.id, c.po_id]))
      const ipcDataByContractId = new Map()
      await Promise.all(rows.map(async r => {
        const ipcData = await buildIpcStatementRows(r.id, r.contract_type, poIdByContractId.get(r.id))
        ipcDataByContractId.set(r.id, ipcData)
      }))

      const win = window.open('', '_blank')
      if (!win) { setFinPrinting(false); return }
      const html = renderFinancialStatementPdf(rows, { name: projectName }, ipcDataByContractId)
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 400)
    } catch (e) {
      console.error('handlePrintFinancialStatement:', e.message)
      alert('Failed to generate statement: ' + e.message)
    }
    setFinPrinting(false)
  }

  function toggleFinSelection(id) {
    setFinSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function selectAllFinRows(visibleRows) {
    setFinSelectedIds(new Set(visibleRows.map(r => r.id)))
  }
  function clearFinSelection() {
    setFinSelectedIds(new Set())
  }

  async function openStatement(row) {
    setStatementContract(row)
    setStatementLoading(true)
    try {
      // Refactored (this revision) to consume the shared
      // buildIpcStatementRows() helper — same query/aggregation logic
      // as before, now the single source used by both this on-screen
      // modal and the printed Financial Statement's IPC section. This
      // component's own responsibilities (React state, contractValue
      // passthrough) are unchanged.
      const { rows, unlinkedTotal, totalPaidLinked } = await buildIpcStatementRows(row.id, row.contract_type, row.po_id)
      setStatementRows({ rows, unlinkedTotal, totalPaidLinked, contractValue: row.contract_value })
    } catch (e) { console.error('openStatement:', e.message) }
    setStatementLoading(false)
  }

  async function eqFetchAll(pid) {
    setEqLoading(true)
    await Promise.all([eqFetchContracts(pid), eqFetchAvailablePOs(pid), eqFetchCostCodes(pid)])
    setEqLoading(false)
  }

  async function eqFetchContracts(pid) {
    const id = pid || projectId
    if (!id) return
    const { data, error } = await supabase
      .from('equipment_contracts')
      .select('*, rental_equipment_pos(po_number, total_exc_vat, total_inc_vat, vendor), cost_codes(code, description)')
      .eq('project_id', id)
      .order('created_at', { ascending: false })
    if (error) { console.error('eqFetchContracts:', error.message); return }
    const list = data || []
    if (list.length > 0) {
      const { data: ipcs, error: ipcErr } = await supabase
        .from('ipc_certificates')
        .select('equipment_contract_id, gross_amount, status')
        .in('equipment_contract_id', list.map(x => x.id))
      if (ipcErr) console.error('eqFetchContracts ipc check:', ipcErr.message)
      const ipcMap = {}
      const hasIpcMap = {}
      ;(ipcs || []).forEach(i => {
        hasIpcMap[i.equipment_contract_id] = true
        if (i.status === 'approved') ipcMap[i.equipment_contract_id] = (ipcMap[i.equipment_contract_id] || 0) + (parseFloat(i.gross_amount) || 0)
      })
      list.forEach(c => { c._certified = ipcMap[c.id] || 0; c._hasIpc = !!hasIpcMap[c.id] })
    }
    setEqContracts(list)
    eqCalcStats(list)
  }

  async function eqFetchAvailablePOs(pid) {
    const id = pid || projectId
    if (!id) return
    const { data } = await supabase
      .from('rental_equipment_pos')
      .select('id, po_number, vendor, total_exc_vat, total_inc_vat, po_type, notes, vat_pct, currency')
      .eq('status', 'approved')
      .eq('project_id', id)
      .order('po_number')
    setEqAvailablePOs(data || [])
  }

  async function eqFetchCostCodes(pid) {
    const id = pid || projectId
    const { data } = await supabase.from('cost_codes').select('id, code, description, unit, level, parent_id').eq('project_id', id).eq('is_active', true).order('code')
    const all = data || []
    setEqCostCodes(all)
    setEqSubCodesList(buildSubCodesList(all))
  }

  async function fetchPOLines(poId) {
    if (!poId) { setPoLines([]); return }
    const { data } = await supabase
      .from('po_line_items')
      .select('*, cost_codes(code, description)')
      .eq('po_id', poId)
      .order('created_at')
    setPoLines(data || [])
  }

  function calcStats(data) {
    const approved   = data.filter(c => c.status === 'approved').length
    const totalValue = data.reduce((s, c) => s + (parseFloat(c.contract_value) || 0), 0)
    const certifiedToDate = data.reduce((s, c) => s + (c._certified || 0), 0)
    setStats({ total: data.length, approved, totalValue, certifiedToDate })
  }

  // ── OPEN / SAVE ────────────────────────────────────────────
  async function openNew() {
    if (!projectId) { alert('Select a project first'); return }
    const { data: all } = await supabase.from('subcontracts').select('contract_no').eq('project_id', projectId)
    const maxNo = (all || []).reduce((max, ct) => {
      const m = ct.contract_no?.match(/SC-(\d+)/)
      return m ? Math.max(max, parseInt(m[1])) : max
    }, 0)
    // vat_pct resolved from the project's own configuration at the
    // moment the new-contract form opens -- the emptyForm() placeholder
    // ('0') is only ever shown transiently before this resolves, and
    // only stays if the project genuinely has no VAT configuration.
    let resolvedVatPct = emptyForm().vat_pct
    const { data: vatRow } = await supabase.rpc('get_project_vat_config', { p_project_id: projectId })
    const vc = vatRow?.[0]
    if (vc?.vat_treatment === 'standard' && vc.vat_rate != null) resolvedVatPct = vc.vat_rate
    else if (vc?.vat_treatment === 'zero_rated') resolvedVatPct = 0
    setForm({ ...emptyForm(), vat_pct: resolvedVatPct, contract_no: `SC-${String(maxNo + 1).padStart(4, '0')}` })
    setEditContract(null)
    await fetchAvailablePOs(projectId)
    setPoLines([])
    setShowModal(true)
  }

  function openEdit(c) {
    setForm({
      po_id: c.po_id || '', contract_no: c.contract_no || '',
      contractor_name: c.contractor_name || '', subcontractor_contact: c.subcontractor_contact || '',
      subcontractor_email: c.subcontractor_email || '', scope: c.scope || '',
      contract_value: c.contract_value || '', vat_pct: c.vat_pct ?? '0',
      contract_date: c.contract_date || '', retention_pct: c.retention_pct ?? '10',
      advance_pct: c.advance_pct ?? '0', adv_recovery_rate: c.adv_recovery_rate ?? '0', vat_case: c.vat_case ?? 1,
      payment_terms: c.payment_terms ?? '30',
      status: c.status || 'draft', currency: c.currency || 'SAR',
      cost_code_id: c.cost_code_id || '', sub_code: c.sub_code || '', notes: c.notes || ''
    })
    setEditContract(c)
    fetchAvailablePOs(projectId)
    fetchPOLines(c.po_id)
    setShowModal(true)
  }

  async function handlePOSelect(poId) {
    const po = availablePOs.find(p => p.id === poId)
    if (po) {
      setForm(f => ({
        ...f, po_id: poId,
        contractor_name: po.vendor || f.contractor_name,
        // contract_value is copied verbatim in the PO's OWN transaction
        // currency (never pre-converted -- confirmed by the currency
        // architecture: purchase_orders.total_exc_vat always holds the
        // original transaction amount). currency must therefore be
        // copied alongside it so the two stay consistent; previously
        // only contract_value/vat_pct were copied here, silently
        // leaving the contract's currency at its unrelated default.
        contract_value: po.total_exc_vat || f.contract_value,
        currency: po.currency || f.currency,
        vat_pct: po.vat_pct ?? f.vat_pct,
        scope: po.notes || f.scope,
      }))
      await fetchPOLines(poId)
    } else {
      setForm(f => ({ ...f, po_id: '' }))
      setPoLines([])
    }
  }

  // ═══ Equipment Contracts ═══
  async function eqFetchPOLines(poId) {
    if (!poId) { setEqPoLines([]); return }
    const { data } = await supabase
      .from('rental_equipment_po_line_items')
      .select('*, cost_codes(code, description)')
      .eq('po_id', poId)
      .order('created_at')
    setEqPoLines(data || [])
    const codes = [...new Set((data || []).map(l => l.equipment_type).filter(Boolean))]
    if (codes.length) {
      // .eq('project_id', projectId) added — without it, an
      // equipment_type whose name happens to match a Sub Code in a
      // DIFFERENT project could return that project's description
      // instead of this one's (or none at all, if this project's own
      // code doesn't happen to also exist elsewhere). Confirmed
      // project-scoped business rule: cost codes are unique per project,
      // not globally, so any lookup by code alone must also filter by
      // project_id.
      const { data: sc } = await supabase.from('cost_codes').select('code, description').eq('project_id', projectId).in('code', codes)
      const map = {}
      ;(sc || []).forEach(s => { map[s.code] = s.description })
      setEqSubCodeDescMap(map)
    } else {
      setEqSubCodeDescMap({})
    }
  }

  function eqCalcStats(data) {
    const approved   = data.filter(c => c.status === 'approved').length
    const totalValue = data.reduce((s, c) => s + (parseFloat(c.contract_value) || 0), 0)
    const certifiedToDate = data.reduce((s, c) => s + (c._certified || 0), 0)
    setEqStats({ total: data.length, approved, totalValue, certifiedToDate })
  }

  async function eqOpenNew() {
    if (!projectId) { alert('Select a project first'); return }
    const { data: all } = await supabase.from('equipment_contracts').select('contract_no').eq('project_id', projectId)
    const maxNo = (all || []).reduce((max, ct) => {
      const m = ct.contract_no?.match(/EC-(\d+)/)
      return m ? Math.max(max, parseInt(m[1])) : max
    }, 0)
    let resolvedEqVatPct = eqEmptyForm().vat_pct
    const { data: eqVatRow } = await supabase.rpc('get_project_vat_config', { p_project_id: projectId })
    const eqvc = eqVatRow?.[0]
    if (eqvc?.vat_treatment === 'standard' && eqvc.vat_rate != null) resolvedEqVatPct = eqvc.vat_rate
    else if (eqvc?.vat_treatment === 'zero_rated') resolvedEqVatPct = 0
    setEqForm({ ...eqEmptyForm(), vat_pct: resolvedEqVatPct, contract_no: `EC-${String(maxNo + 1).padStart(4, '0')}` })
    setEqEditContract(null)
    await eqFetchAvailablePOs(projectId)
    setEqPoLines([])
    setEqShowModal(true)
  }

  function eqOpenEdit(c) {
    setEqForm({
      po_id: c.po_id || '', contract_no: c.contract_no || '',
      contractor_name: c.contractor_name || '', subcontractor_contact: c.subcontractor_contact || '',
      subcontractor_email: c.subcontractor_email || '', scope: c.scope || '',
      contract_value: c.contract_value || '', vat_pct: c.vat_pct ?? 15,
      contract_date: c.contract_date || '', retention_pct: c.retention_pct ?? 10,
      advance_pct: c.advance_pct ?? 0, adv_recovery_rate: c.adv_recovery_rate ?? 0, vat_case: c.vat_case ?? 1,
      payment_terms: c.payment_terms ?? 30,
      status: c.status || 'draft', currency: c.currency || 'SAR',
      cost_code_id: c.cost_code_id || '', sub_code: c.sub_code || '', notes: c.notes || ''
    })
    setEqEditContract(c)
    eqFetchAvailablePOs(projectId)
    eqFetchPOLines(c.po_id)
    setEqShowModal(true)
  }

  async function eqHandlePOSelect(poId) {
    const po = eqAvailablePOs.find(p => p.id === poId)
    if (po) {
      setEqForm(f => ({
        ...f, po_id: poId,
        contractor_name: po.vendor || f.contractor_name,
        contract_value: po.total_exc_vat || f.contract_value,
        currency: po.currency || f.currency,
        vat_pct: po.vat_pct ?? f.vat_pct,
        scope: po.notes || f.scope,
      }))
      await eqFetchPOLines(poId)
    } else {
      setEqForm(f => ({ ...f, po_id: '' }))
      setEqPoLines([])
    }
  }

  async function saveContract() {
    if (!form.contract_no || !form.contractor_name) {
      alert('Contract No و Contractor Name مطلوبان')
      return
    }
    if (form.po_id) {
      const { data: existing } = await supabase
        .from('subcontracts').select('id, contract_no')
        .eq('project_id', projectId).eq('po_id', form.po_id)
      const conflict = (existing || []).find(x => x.id !== editContract?.id)
      if (conflict) { alert(`هذا الـ PO مرتبط بالفعل بعقد ${conflict.contract_no}`); return }
    }
    const cv = parseFloat(form.contract_value) || 0
    const vat = parseFloat(form.vat_pct) || 0
    // Contract Currency Snapshot: a new financial event (new contract, OR
    // an edit that actually changes contract_value/currency) resolves and
    // stores the applicable rate NOW. An edit that changes neither value
    // nor currency (e.g. only scope/dates/notes) must NOT touch the
    // existing historical exchange_rate_applied/project_currency_amount
    // -- those two stay exactly as already stored on editContract.
    const contractCurrency = form.currency || 'SAR'
    const isNewFinancialEvent = !editContract
      || parseFloat(editContract.contract_value) !== cv
      || editContract.currency !== contractCurrency
    let exchangeRateApplied = editContract?.exchange_rate_applied ?? null
    let projectCurrencyAmount = editContract?.project_currency_amount ?? null
    if (isNewFinancialEvent) {
      if (!projectCurrency || contractCurrency === projectCurrency) {
        exchangeRateApplied = 1
      } else {
        const { data: rate } = await supabase.rpc('get_exchange_rate', { p_from: contractCurrency, p_to: projectCurrency })
        exchangeRateApplied = rate != null ? rate : null
      }
      projectCurrencyAmount = exchangeRateApplied != null ? +(cv * exchangeRateApplied).toFixed(2) : null
    }
    const payload = {
      project_id: projectId, po_id: form.po_id || null,
      contract_no: form.contract_no, contractor_name: form.contractor_name,
      subcontractor_contact: form.subcontractor_contact, subcontractor_email: form.subcontractor_email,
      scope: form.scope, contract_value: cv, vat_pct: vat,
      contract_inc_vat: cv * (1 + vat / 100),
      contract_date: form.contract_date || null,
      retention_pct: parseFloat(form.retention_pct) || 0,
      advance_pct: parseFloat(form.advance_pct) || 0,
      adv_recovery_rate: parseFloat(form.adv_recovery_rate) || 0,
      vat_case: parseInt(form.vat_case) || 1,
      payment_terms: parseInt(form.payment_terms) || 30,
      status: editContract ? form.status : 'draft',
      currency: contractCurrency, cost_code_id: form.cost_code_id || null, sub_code: form.sub_code || null, notes: form.notes,
      exchange_rate_applied: exchangeRateApplied,
      project_currency_amount: projectCurrencyAmount,
    }
    let error, newContractId = editContract?.id
    if (editContract) {
      ;({ error } = await supabase.from('subcontracts').update(payload).eq('id', editContract.id))
    } else {
      const { data: inserted, error: insErr } = await supabase.from('subcontracts').insert(payload).select('id').single()
      error = insErr
      newContractId = inserted?.id
    }
    if (error) { alert('خطأ: ' + error.message); return }
    // Currency Audit Trail: only when this save actually resolved a NEW
    // FX snapshot (isNewFinancialEvent, computed above) -- an edit that
    // left contract_value/currency untouched writes no currency comment,
    // consistent with the "preserve, don't re-log" rule already applied
    // to the snapshot itself.
    if (isNewFinancialEvent) {
      const actorName = (await supabase.auth.getUser()).data?.user?.email || 'Unknown'
      const comment = editContract
        ? `Currency changed: ${editContract.currency || 'SAR'} @ ${editContract.exchange_rate_applied ?? 1} → ${contractCurrency} @ ${exchangeRateApplied ?? 'unresolved'}`
        : `Currency: ${contractCurrency} @ rate ${exchangeRateApplied ?? 'unresolved'} (Project currency: ${projectCurrency || 'SAR'})`
      await logAudit(newContractId, 'subcontract', editContract ? 'edited' : 'created', actorName, comment, null, payload.status, projectId)
    }
    invalidate('projects_list'); setShowModal(false)
    await fetchContracts(projectId)
  }

  async function deleteContract(id, hasIpc) {
    if (!can('Subcontract.Delete')) { alert('حذف العقد متاح فقط لدور Contract Manager.'); return }
    if (hasIpc) { alert('لا يمكن حذف هذا العقد لأنه مرتبط بمستخلص (IPC).'); return }
    if (!window.confirm('حذف العقد؟')) return
    // Legacy #2/#3: delete the record first, verify it, and only then remove its documents (never the other way round)
    const result = await deleteRecordThenDocuments('subcontracts', id)
    const problem = describeDeleteResult(result, 'العقد')
    if (problem) alert(problem)
    fetchContracts(projectId)
  }

  async function openContractDetail(c) {
    setLoading(true)
    // Refetch fresh contract with all relations
    const { data } = await supabase
      .from('subcontracts')
      .select('*, purchase_orders(po_number, total_exc_vat, total_inc_vat, vendor), cost_codes(code, description)')
      .eq('id', c.id)
      .single()
    setSelectedContract(data ? { ...data, _hasIpc: c._hasIpc, _certified: c._certified } : c)
    await fetchPOLines((data || c).po_id)
    await fetchAuditHistory(c.id, 'material', (data || c).po_id)
    setLoading(false)
  }

  // ═══ Equipment Contracts ═══
  async function eqSaveContract() {
    if (!eqForm.contract_no || !eqForm.contractor_name) {
      alert('Contract No و Contractor Name مطلوبان')
      return
    }
    if (eqForm.po_id) {
      const { data: existing } = await supabase
        .from('equipment_contracts').select('id, contract_no')
        .eq('project_id', projectId).eq('po_id', eqForm.po_id)
      const conflict = (existing || []).find(x => x.id !== eqEditContract?.id)
      if (conflict) { alert(`هذا الـ PO مرتبط بالفعل بعقد ${conflict.contract_no}`); return }
    }
    const cv = parseFloat(eqForm.contract_value) || 0
    const vat = parseFloat(eqForm.vat_pct) || 0
    // Same Contract Currency Snapshot logic as saveContract() above --
    // only a genuine change to contract_value/currency triggers a new
    // rate resolution; otherwise the existing historical snapshot is
    // preserved untouched.
    const eqContractCurrency = eqForm.currency || 'SAR'
    const eqIsNewFinancialEvent = !eqEditContract
      || parseFloat(eqEditContract.contract_value) !== cv
      || eqEditContract.currency !== eqContractCurrency
    let eqExchangeRateApplied = eqEditContract?.exchange_rate_applied ?? null
    let eqProjectCurrencyAmount = eqEditContract?.project_currency_amount ?? null
    if (eqIsNewFinancialEvent) {
      if (!projectCurrency || eqContractCurrency === projectCurrency) {
        eqExchangeRateApplied = 1
      } else {
        const { data: rate } = await supabase.rpc('get_exchange_rate', { p_from: eqContractCurrency, p_to: projectCurrency })
        eqExchangeRateApplied = rate != null ? rate : null
      }
      eqProjectCurrencyAmount = eqExchangeRateApplied != null ? +(cv * eqExchangeRateApplied).toFixed(2) : null
    }
    const payload = {
      project_id: projectId, po_id: eqForm.po_id || null,
      contract_no: eqForm.contract_no, contractor_name: eqForm.contractor_name,
      subcontractor_contact: eqForm.subcontractor_contact, subcontractor_email: eqForm.subcontractor_email,
      scope: eqForm.scope, contract_value: cv, vat_pct: vat,
      contract_inc_vat: cv * (1 + vat / 100),
      contract_date: eqForm.contract_date || null,
      retention_pct: parseFloat(eqForm.retention_pct) || 0,
      advance_pct: parseFloat(eqForm.advance_pct) || 0,
      adv_recovery_rate: parseFloat(eqForm.adv_recovery_rate) || 0,
      vat_case: parseInt(eqForm.vat_case) || 1,
      payment_terms: parseInt(eqForm.payment_terms) || 30,
      status: eqEditContract ? eqForm.status : 'draft',
      currency: eqContractCurrency, cost_code_id: eqForm.cost_code_id || null, sub_code: eqForm.sub_code || null, notes: eqForm.notes,
      exchange_rate_applied: eqExchangeRateApplied,
      project_currency_amount: eqProjectCurrencyAmount,
    }
    let error, newEqContractId = eqEditContract?.id
    if (eqEditContract) {
      ;({ error } = await supabase.from('equipment_contracts').update(payload).eq('id', eqEditContract.id))
    } else {
      const { data: eqInserted, error: eqInsErr } = await supabase.from('equipment_contracts').insert(payload).select('id').single()
      error = eqInsErr
      newEqContractId = eqInserted?.id
    }
    if (error) { alert('خطأ: ' + error.message); return }
    if (eqIsNewFinancialEvent) {
      const actorName = (await supabase.auth.getUser()).data?.user?.email || 'Unknown'
      const comment = eqEditContract
        ? `Currency changed: ${eqEditContract.currency || 'SAR'} @ ${eqEditContract.exchange_rate_applied ?? 1} → ${eqContractCurrency} @ ${eqExchangeRateApplied ?? 'unresolved'}`
        : `Currency: ${eqContractCurrency} @ rate ${eqExchangeRateApplied ?? 'unresolved'} (Project currency: ${projectCurrency || 'SAR'})`
      await logAudit(newEqContractId, 'equipment_contract', eqEditContract ? 'edited' : 'created', actorName, comment, null, payload.status, projectId)
    }
    setEqShowModal(false)
    await eqFetchContracts(projectId)
  }

  async function eqDeleteContract(id, hasIpc) {
    if (!can('Subcontract.Delete')) { alert('حذف العقد متاح فقط لدور Contract Manager.'); return }
    if (hasIpc) { alert('لا يمكن حذف هذا العقد لأنه مرتبط بمستخلص (IPC).'); return }
    if (!window.confirm('حذف العقد؟')) return
    // Legacy #2/#3: delete the record first, verify it, and only then remove its documents (never the other way round)
    const result = await deleteRecordThenDocuments('equipment_contracts', id)
    const problem = describeDeleteResult(result, 'العقد')
    if (problem) alert(problem)
    eqFetchContracts(projectId)
  }

  async function eqOpenContractDetail(c) {
    setEqLoading(true)
    const { data } = await supabase
      .from('equipment_contracts')
      .select('*, rental_equipment_pos(po_number, total_exc_vat, total_inc_vat, vendor), cost_codes(code, description)')
      .eq('id', c.id)
      .single()
    setEqSelectedContract(data ? { ...data, _hasIpc: c._hasIpc, _certified: c._certified } : c)
    await eqFetchPOLines((data || c).po_id)
    await fetchAuditHistory(c.id, 'equipment', (data || c).po_id)
    setEqLoading(false)
  }

  // ── UPLOAD ─────────────────────────────────────────────────
  function downloadTemplate() {
    const ws = XLSX.utils.json_to_sheet([{
      po_number: 'PO-0001', contract_no: 'SC-0001', contractor_name: 'شركة المقاولات',
      subcontractor_contact: 'أحمد محمد', subcontractor_email: 'ahmed@co.com',
      scope: 'أعمال الحديد', sub_code: 'S-C.Dew.1', contract_value: 500000,
      vat_pct: 15, contract_date: '2026-01-01', retention_pct: 10,
      advance_pct: 10, payment_terms: 30, currency: 'SAR', notes: ''
    }])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Subcontracts')
    XLSX.writeFile(wb, 'subcontracts_template.xlsx')
  }

  function handleFileUpload(e) {
    const file = e.target.files[0]; if (!file) return
    setUploadError('')
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' })
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
        if (!rows.length) { setUploadError('الملف فاضي'); return }
        setUploadRows(rows)
      } catch { setUploadError('خطأ في قراءة الملف') }
    }
    reader.readAsBinaryString(file)
  }

  async function confirmUpload() {
    if (!can('Subcontract.Submit')) {
      alert('Bulk upload is only available to the Contract Manager.')
      return
    }
    const contractNos = uploadRows.map(r => String(r.contract_no || '').trim()).filter(Boolean)
    let existingMap = {}
    if (contractNos.length) {
      const { data: existing } = await supabase
        .from('subcontracts').select('id, contract_no, approval_stage')
        .eq('project_id', projectId).in('contract_no', contractNos)
      const blocked = (existing || []).filter(e => !['draft','rejected'].includes(e.approval_stage || 'draft'))
      if (blocked.length) {
        alert(`Upload blocked — these contracts are already submitted/approved and can't be re-uploaded: ${blocked.map(b=>b.contract_no).join(', ')}`)
        return
      }
      ;(existing || []).forEach(e => { existingMap[e.contract_no] = e })
    }
    let success = 0, failed = 0, levelMismatch = 0
    for (const row of uploadRows) {
      const po = row.po_number ? availablePOs.find(p => p.po_number === String(row.po_number).trim()) : null
      const rowSubCode = String(row.sub_code || row.cost_code || '').trim()
      const sub = rowSubCode ? subCodesList.find(s => s.code === rowSubCode) : null
      if (rowSubCode && !sub) {
        const anyMatch = costCodes.find(c => c.code === rowSubCode)
        if (anyMatch) levelMismatch++
      }
      const cv = parseFloat(row.contract_value) || 0
      const vat = parseFloat(row.vat_pct) || 0
      const st = STATUSES.includes(String(row.status || '').toLowerCase()) ? String(row.status).toLowerCase() : 'draft'
      const contractNo = String(row.contract_no || '').trim()
      // Currency Snapshot: this row's currency comes from the uploaded
      // Excel file itself (not copied from a PO), so the applicable rate
      // must be resolved now, at the moment this contract is actually
      // being created -- the centralized get_exchange_rate(), never a
      // hardcoded number. NULL only if the resolution genuinely fails
      // (e.g. no rate on file for that pair), never fabricated.
      const rowCurrency = row.currency || 'SAR'
      let rowExchangeRate = 1
      if (projectCurrency && rowCurrency !== projectCurrency) {
        const { data: rate } = await supabase.rpc('get_exchange_rate', { p_from: rowCurrency, p_to: projectCurrency })
        rowExchangeRate = rate != null ? rate : null
      }
      const payload = {
        project_id: projectId, po_id: po?.id || null,
        contract_no: contractNo,
        contractor_name: String(row.contractor_name || '').trim(),
        subcontractor_contact: String(row.subcontractor_contact || '').trim(),
        subcontractor_email: String(row.subcontractor_email || '').trim(),
        scope: String(row.scope || '').trim(),
        cost_code_id: sub?.costCodeId || null, sub_code: sub?.code || null, contract_value: cv, vat_pct: vat,
        contract_inc_vat: cv * (1 + vat / 100),
        contract_date: row.contract_date || null,
        retention_pct: parseFloat(row.retention_pct) || 0,
        advance_pct: parseFloat(row.advance_pct) || 0,
        payment_terms: parseInt(row.payment_terms) || 30,
        status: st, currency: rowCurrency,
        exchange_rate_applied: rowExchangeRate,
        project_currency_amount: rowExchangeRate != null ? +(cv * rowExchangeRate).toFixed(2) : null,
        notes: String(row.notes || '').trim()
      }
      if (!payload.contract_no || !payload.contractor_name) { failed++; continue }
      const existingContract = existingMap[contractNo]
      let error
      if (existingContract) {
        payload.status = 'draft'; payload.approval_stage = 'draft'
        ;({ error } = await supabase.from('subcontracts').update(payload).eq('id', existingContract.id))
      } else {
        ;({ error } = await supabase.from('subcontracts').insert(payload))
      }
      if (error) failed++; else success++
    }
    alert(`✅ تم رفع ${success} عقد${failed > 0 ? ` — فشل ${failed}` : ''}${levelMismatch > 0 ? ` (⚠ ${levelMismatch} كود مش Sub Code فعلي في الشجرة)` : ''}`)
    setUploadRows([]); setShowUpload(false)
    if (fileRef.current) fileRef.current.value = ''
    fetchContracts(projectId)
  }

  // ── BULK CONVERT PO COMPLEX → CONTRACTS ───────────────────
  async function openConvertModal() {
    if (!projectId) { alert('Select a project first'); return }
    setConvertResult(null)

    // Fetch all approved complex/equipment POs for this project
    const { data: allComplex } = await supabase
      .from('purchase_orders')
      .select('id, po_number, vendor, total_exc_vat, total_inc_vat, vat_pct, notes, logistics_type, currency, exchange_rate_applied, project_currency_amount')
      .eq('project_id', projectId)
      .eq('status', 'approved')
      .in('po_type', ['complex', 'equipment'])
      .order('po_number')

    // Fetch POs already linked to a subcontract
    const { data: linked } = await supabase
      .from('subcontracts')
      .select('po_id')
      .eq('project_id', projectId)
      .not('po_id', 'is', null)

    const linkedIds = new Set((linked || []).map(x => x.po_id))
    const unlinked  = (allComplex || []).filter(po => !linkedIds.has(po.id))
    setUnconvertedPOs(unlinked)

    // Get current max SC number to auto-assign
    const { data: existingSCs } = await supabase
      .from('subcontracts')
      .select('contract_no')
      .eq('project_id', projectId)

    let maxNo = (existingSCs || []).reduce((max, ct) => {
      const m = ct.contract_no?.match(/SC-(\d+)/)
      return m ? Math.max(max, parseInt(m[1])) : max
    }, 0)

    const rows = unlinked.map(po => {
      maxNo++
      return {
        po,
        contract_no: `SC-${String(maxNo).padStart(4, '0')}`,
        retention_pct: '10',
        payment_terms: '30',
        checked: true,
      }
    })
    setConvertRows(rows)
    setShowConvertModal(true)
  }

  function toggleConvertRow(idx) {
    setConvertRows(prev => prev.map((r, i) => i === idx ? { ...r, checked: !r.checked } : r))
  }

  function updateConvertNo(idx, val) {
    setConvertRows(prev => prev.map((r, i) => i === idx ? { ...r, contract_no: val } : r))
  }

  function updateConvertField(idx, field, val) {
    setConvertRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r))
  }

  async function confirmBulkConvert() {
    const selected = convertRows.filter(r => r.checked)
    if (!selected.length) { alert('اختر PO واحد على الأقل'); return }

    // Validate no duplicate contract_no
    const nos = selected.map(r => r.contract_no.trim())
    const unique = new Set(nos)
    if (unique.size !== nos.length) { alert('يوجد أرقام عقود متكررة — تأكد من تعديلها'); return }
    const emptyNo = nos.find(n => !n)
    if (emptyNo !== undefined) { alert('رقم العقد لا يمكن أن يكون فاضياً'); return }

    setConverting(true)
    let success = 0, failed = 0

    const payloads = selected.map(r => {
      const cv  = parseFloat(r.po.total_exc_vat) || 0
      const vat = parseFloat(r.po.vat_pct)       || 0
      return {
        project_id:    projectId,
        po_id:         r.po.id,
        contract_no:   r.contract_no.trim(),
        contractor_name: r.po.vendor || 'Unknown',
        contract_value:  cv,
        vat_pct:         vat,
        contract_inc_vat: cv * (1 + vat / 100),
        scope:           r.po.notes || '',
        retention_pct:   parseFloat(r.retention_pct) || 0,
        advance_pct:     0,
        payment_terms:   parseInt(r.payment_terms)   || 30,
        // Currency Snapshot: this contract is created verbatim FROM the
        // PO (contract_value = po.total_exc_vat above) -- its FX snapshot
        // is therefore copied directly from the PO's own already-resolved
        // snapshot, never recomputed. If the PO itself predates the
        // Currency architecture (exchange_rate_applied/
        // project_currency_amount are NULL there), the new contract
        // correctly inherits that same NULL rather than fabricating a
        // rate -- consistent with the "never backfill" historical-safety
        // rule already applied elsewhere in this file.
        currency:        r.po.currency || 'SAR',
        exchange_rate_applied: r.po.exchange_rate_applied ?? null,
        project_currency_amount: r.po.project_currency_amount ?? null,
        status:          'draft',
        logistics_type:  r.po.logistics_type || null,
      }
    })

    // Check existing contract_no conflicts in DB
    const { data: existing } = await supabase
      .from('subcontracts')
      .select('contract_no')
      .eq('project_id', projectId)
      .in('contract_no', nos)

    if (existing && existing.length > 0) {
      const conflicts = existing.map(x => x.contract_no).join(', ')
      alert(`أرقام العقود التالية موجودة بالفعل:\n${conflicts}\nعدّلها وحاول مرة ثانية`)
      setConverting(false)
      return
    }

    // Insert one by one to identify failures
    const errors = []
    for (const payload of payloads) {
      const { error } = await supabase.from('subcontracts').insert([payload])
      if (error) {
        console.error('Insert error:', error.message, payload.contract_no)
        errors.push(`${payload.contract_no}: ${error.message}`)
        failed++
      } else {
        success++
      }
    }

    setConverting(false)
    setConvertResult({ success, failed, errors })
    if (success > 0) {
      await fetchContracts(projectId)
      await fetchAvailablePOs(projectId)
    }
  }

  // ═══ Equipment Convert PO → Contract ═══
  async function eqOpenConvertModal() {
    if (!projectId) { alert('Select a project first'); return }
    setEqConvertResult(null)

    const { data: allEqPOs } = await supabase
      .from('rental_equipment_pos')
      .select('id, po_number, vendor, total_exc_vat, total_inc_vat, vat_pct, notes, currency, exchange_rate_applied, project_currency_amount')
      .eq('project_id', projectId)
      .eq('status', 'approved')
      .order('po_number')

    const { data: linked } = await supabase
      .from('equipment_contracts')
      .select('po_id')
      .eq('project_id', projectId)
      .not('po_id', 'is', null)

    const linkedIds = new Set((linked || []).map(x => x.po_id))
    const unlinked  = (allEqPOs || []).filter(po => !linkedIds.has(po.id))
    setEqUnconvertedPOs(unlinked)

    const { data: existingECs } = await supabase
      .from('equipment_contracts')
      .select('contract_no')
      .eq('project_id', projectId)

    let maxNo = (existingECs || []).reduce((max, ct) => {
      const m = ct.contract_no?.match(/EC-(\d+)/)
      return m ? Math.max(max, parseInt(m[1])) : max
    }, 0)

    const rows = unlinked.map(po => {
      maxNo++
      return {
        po,
        contract_no: `EC-${String(maxNo).padStart(4, '0')}`,
        retention_pct: '0',
        advance_pct: '0',
        payment_terms: '30',
        checked: true,
      }
    })
    setEqConvertRows(rows)
    setEqShowConvertModal(true)
  }

  function eqToggleConvertRow(idx) {
    setEqConvertRows(prev => prev.map((r, i) => i === idx ? { ...r, checked: !r.checked } : r))
  }

  function eqUpdateConvertNo(idx, val) {
    setEqConvertRows(prev => prev.map((r, i) => i === idx ? { ...r, contract_no: val } : r))
  }

  function eqUpdateConvertField(idx, field, val) {
    setEqConvertRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r))
  }

  async function eqConfirmBulkConvert() {
    const selected = eqConvertRows.filter(r => r.checked)
    if (!selected.length) { alert('اختر PO واحد على الأقل'); return }

    const nos = selected.map(r => r.contract_no.trim())
    const unique = new Set(nos)
    if (unique.size !== nos.length) { alert('يوجد أرقام عقود متكررة — تأكد من تعديلها'); return }
    const emptyNo = nos.find(n => !n)
    if (emptyNo !== undefined) { alert('رقم العقد لا يمكن أن يكون فاضياً'); return }

    setEqConverting(true)
    let success = 0, failed = 0

    const payloads = selected.map(r => {
      const cv  = parseFloat(r.po.total_exc_vat) || 0
      const vat = parseFloat(r.po.vat_pct)       || 0
      return {
        project_id:    projectId,
        po_id:         r.po.id,
        contract_no:   r.contract_no.trim(),
        contractor_name: r.po.vendor || 'Unknown',
        contract_value:  cv,
        vat_pct:         vat,
        contract_inc_vat: cv * (1 + vat / 100),
        scope:           r.po.notes || '',
        retention_pct:   parseFloat(r.retention_pct) || 0,
        advance_pct:     parseFloat(r.advance_pct) || 0,
        payment_terms:   parseInt(r.payment_terms)   || 30,
        // Currency Snapshot copied verbatim from the source PO -- same
        // reasoning as confirmBulkConvert() (Subcontract) above.
        currency:        r.po.currency || 'SAR',
        exchange_rate_applied: r.po.exchange_rate_applied ?? null,
        project_currency_amount: r.po.project_currency_amount ?? null,
        status:          'draft',
        approval_stage:  'draft',
      }
    })

    const { data: existing } = await supabase
      .from('equipment_contracts')
      .select('contract_no')
      .eq('project_id', projectId)
      .in('contract_no', nos)

    if (existing && existing.length > 0) {
      const conflicts = existing.map(x => x.contract_no).join(', ')
      alert(`أرقام العقود التالية موجودة بالفعل:\n${conflicts}\nعدّلها وحاول مرة ثانية`)
      setEqConverting(false)
      return
    }

    const errors = []
    for (const payload of payloads) {
      const { error } = await supabase.from('equipment_contracts').insert([payload])
      if (error) {
        console.error('Insert error:', error.message, payload.contract_no)
        errors.push(`${payload.contract_no}: ${error.message}`)
        failed++
      } else {
        success++
      }
    }

    setEqConverting(false)
    setEqConvertResult({ success, failed, errors })
    if (success > 0) {
      await eqFetchContracts(projectId)
      await eqFetchAvailablePOs(projectId)
    }
  }

  // ── BULK APPROVE (advances current stage for each selected contract) ──
  
  // ── Unified bulk action (submit/approve, reject, resubmit, revoke, delete) ──
  // Silently skips any contract whose current stage doesn't allow the chosen action for the acting role.
  async function bulkContractAction(actionType, contractType = 'material') {
    const isEq = contractType === 'equipment'
    const selSet = isEq ? eqSelectedIds : selectedIds
    const setSel = isEq ? setEqSelectedIds : setSelectedIds
    const list = isEq ? eqContracts : contracts
    const handler = isEq ? eqHandleApprovalAction : handleApprovalAction
    const table = isEq ? 'equipment_contracts' : 'subcontracts'
    const refetch = isEq ? () => eqFetchContracts(projectId) : () => fetchContracts(projectId)

    if (!selSet.size) return
    const ids = [...selSet]

    if (actionType === 'delete') {
      if (!can('Subcontract.Delete')) { alert('حذف العقود متاح فقط لدور Contract Manager.'); return }
      const blocked = ids.filter(id => list.find(x => x.id === id)?._hasIpc)
      const deletable = ids.filter(id => !list.find(x => x.id === id)?._hasIpc)
      if (!deletable.length) { alert('كل العقود المحددة مرتبطة بمستخلص (IPC) ولا يمكن حذفها.'); return }
      if (!window.confirm(`هل أنت متأكد من حذف ${deletable.length} عقد؟${blocked.length ? ` (تم تجاهل ${blocked.length} مرتبط بمستخلص)` : ''} لا يمكن التراجع.`)) return
      const { error } = await supabase.from(table).delete().in('id', deletable)
      if (error) { alert('حصل خطأ: ' + error.message); return }
      await refetch()
      setSel(new Set())
      return
    }

    let reason = ''
    if (actionType === 'reject') {
      reason = prompt('سبب الرفض (لكل العقود المحددة) — مطلوب:') || ''
      if (reason === '') return
    }

    setBulkApproving(true)
    let acted = 0, skipped = 0
    for (const id of ids) {
      const c = list.find(x => x.id === id)
      if (!c) { skipped++; continue }
      const stage = c.approval_stage || 'draft'
      let allowed = false
      let effectiveAction = actionType

      if (actionType === 'approve') {
        // "submit" for contract_manager on draft, "approve" for cost_control/director on their stage
        if ((stage === 'draft' || !c.approval_stage) && can('Subcontract.Submit')) allowed = true
        else if (stage === 'cost_control' && can('Subcontract.ApproveCostControl')) allowed = true
        else if (stage === 'director' && can('Subcontract.ApproveDirector')) allowed = true
      } else if (actionType === 'reject') {
        if (['cost_control','director'].includes(stage)) allowed = true
      } else if (actionType === 'resubmit') {
        if (stage === 'rejected' && can('Subcontract.Submit')) allowed = true
      } else if (actionType === 'revoke') {
        // Fixed here: this Bulk Action path was still using the old
        // fixed can('Subcontract.ApproveDirector') check, missed when the dynamic
        // finalApprover logic was applied to the four individual Revoke
        // buttons elsewhere in this file — now consistent with them.
        // The !_hasIpc gate is also removed here, same reasoning as
        // those four buttons (Revoke never touches ipc_certificates).
        const finalApprover = c.director_approved_by ? 'director' : 'cost_control'
        if (stage === 'approved' && ((finalApprover === 'director' && can('Subcontract.ApproveDirector')) || (finalApprover === 'cost_control' && can('Subcontract.ApproveCostControl')))) allowed = true
      }

      if (!allowed) { skipped++; continue }

      if (actionType === 'reject') {
        const { error } = await supabase.from(table).update({ approval_stage: 'rejected', rejection_reason: reason }).eq('id', c.id)
        if (!error) {
          await logAudit(c.id, isEq ? 'equipment_contract' : 'subcontract', 'reject', currentUserName, reason, stage, 'rejected', projectId)
          acted++
        } else skipped++
      } else {
        await handler(c, effectiveAction)
        acted++
      }
    }
    await refetch()
    setSel(new Set())
    setBulkApproving(false)
    alert(skipped ? `تم تنفيذ ${acted} — تم تجاهل ${skipped} (الحالة الحالية لا تسمح بهذا الإجراء لدورك)` : `تم تنفيذ ${acted} بنجاح`)
  }

  async function bulkDelete() {
    if (!selectedIds.size) return
    if (!can('Subcontract.Delete')) { alert('حذف العقود متاح فقط لدور Contract Manager.'); return }
    const ids = [...selectedIds]
    const blocked = ids.filter(id => contracts.find(x => x.id === id)?._hasIpc)
    const deletable = ids.filter(id => !contracts.find(x => x.id === id)?._hasIpc)
    if (!deletable.length) { alert('كل العقود المحددة مرتبطة بمستخلص (IPC) ولا يمكن حذفها.'); return }
    if (!window.confirm(`هل أنت متأكد من حذف ${deletable.length} عقد؟${blocked.length ? ` (تم تجاهل ${blocked.length} مرتبط بمستخلص)` : ''} لا يمكن التراجع.`)) return
    const { error } = await supabase.from('subcontracts').delete().in('id', deletable)
    if (error) { alert('حصل خطأ: ' + error.message) }
    else {
      await fetchContracts(projectId)
      setSelectedIds(new Set())
    }
  }

  function exportToExcel() {
    const rows = contracts.map(c => {
      const sub = subCodesList.find(s => s.code === c.sub_code)
      return {
        'PO Number': c.purchase_orders?.po_number || '',
        'Contract No': c.contract_no, 'Contractor': c.contractor_name,
        'Cost Code': sub?.costCode || c.cost_codes?.code || '', 'Resource Code': sub?.resourceCode || '', 'Resource Description': sub?.resourceCodeDescription || '', 'Sub Code': c.sub_code || '', 'Scope': c.scope,
        'Contract Value': c.contract_value, 'VAT %': c.vat_pct,
        'Inc. VAT': c.contract_inc_vat, 'Status': c.status,
        'Contract Date': c.contract_date, 'Retention %': c.retention_pct,
        'Advance %': c.advance_pct, 'Certified': c._certified || 0,
      }
    })
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Subcontracts')
    XLSX.writeFile(wb, 'subcontracts.xlsx')
  }

  // ── COMPUTED ───────────────────────────────────────────────
  const logisticsOptions = ['all', ...Array.from(new Set(contracts.map(c => c.logistics_type).filter(Boolean)))]

  const filtered = contracts
    .filter(c => filterStatus === 'all' || c.status === filterStatus)
    .filter(c => filterLogistics === 'all' || c.logistics_type === filterLogistics)
    .filter(c => {
      if (!searchQuery.trim()) return true
      const q = searchQuery.toLowerCase()
      return (
        (c.contract_no        || '').toLowerCase().includes(q) ||
        (c.contractor_name    || '').toLowerCase().includes(q) ||
        (c.purchase_orders?.po_number || '').toLowerCase().includes(q) ||
        (c.cost_codes?.code   || '').toLowerCase().includes(q) ||
        (c.scope              || '').toLowerCase().includes(q)
      )
    })
  const poLineTotal = poLines.reduce((s, l) => s + (parseFloat(l.total_exc_vat || l.amount_exc_vat) || 0), 0)

  // ═══ Equipment Contracts ═══
  const eqFiltered = eqContracts
    .filter(c => eqFilterStatus === 'all' || c.status === eqFilterStatus)
    .filter(c => {
      if (!eqSearchQuery.trim()) return true
      const q = eqSearchQuery.toLowerCase()
      return (
        (c.contract_no        || '').toLowerCase().includes(q) ||
        (c.contractor_name    || '').toLowerCase().includes(q) ||
        (c.rental_equipment_pos?.po_number || '').toLowerCase().includes(q) ||
        (c.cost_codes?.code   || '').toLowerCase().includes(q)
      )
    })
  const eqPoLineTotal = eqPoLines.reduce((s, l) => s + (parseFloat(l.total_cost) || 0), 0)

  // ── PROJECT SELECTOR ───────────────────────────────────────
  if (!projectId) return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight mb-6">📝 Subcontracts</h1>
      <p className="text-[#6B7280] mb-4">Select a project to continue</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map(p => (
          <button key={p.id} onClick={() => { setProjectId(p.id); setProjectName(p.name); fetchAll(p.id) }}
            className="text-left bg-white border border-[#E5E7EB] rounded-xl p-4 hover:border-blue-400 hover:shadow-sm transition-all">
            <p className="font-semibold text-[#1C1A18]">{p.name}</p>
            <p className="text-xs text-[#6B7280] mt-0.5">{p.organizations?.name}</p>
          </button>
        ))}
      </div>
    </div>
  )

  // ── MAIN RENDER ────────────────────────────────────────────
  return (
    <div style={{minHeight:'100vh',background:'#F3F4F6',fontFamily:'inherit'}}>
      {/* ── Page Header ── */}
      <div style={{background:'#fff',borderBottom:'1px solid #E5E7EB',padding:'18px 24px 14px',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexShrink:0}}>
        <div>
          {selectedContract && (
            <button onClick={() => setSelectedContract(null)}
              style={{fontSize:11,color:'#6B7280',background:'none',border:'1px solid #E5E7EB',borderRadius:6,padding:'3px 10px',cursor:'pointer',fontFamily:'inherit',marginBottom:8,display:'inline-flex',alignItems:'center',gap:4}}>
              ← Back
            </button>
          )}
          <h1 style={{fontSize:20,fontWeight:800,color:'#0F1017',letterSpacing:'-.02em'}}>
            {selectedContract ? selectedContract.contract_no : 'Subcontracts'}
          </h1>
          <p style={{fontSize:11.5,color:'#6B7280',marginTop:3}}>
            {selectedContract ? `${selectedContract.contractor_name} — ${selectedContract.scope||''}` : `${projectName} · ${stats.total} Contracts`}
            {!selectedContract && (
              <button onClick={() => { setProjectId(null); setSelectedContract(null) }}
                style={{marginLeft:8,fontSize:10,color:'#9CA3AF',background:'none',border:'1px solid #E5E7EB',borderRadius:4,padding:'1px 7px',cursor:'pointer',fontFamily:'inherit'}}>
                × Change
              </button>
            )}
          </p>
        </div>
        {(mainTab === 'material' ? !selectedContract : !eqSelectedContract) && (
          <div style={{display:'flex',flexDirection:'column',gap:8,alignItems:'flex-end'}}>
            <div style={{display:'flex',gap:4,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,padding:4}}>
              <button onClick={()=>setMainTab('material')} style={{padding:'5px 14px',borderRadius:6,fontSize:12,fontWeight:600,border:'none',cursor:'pointer',fontFamily:'inherit',background:mainTab==='material'?'#1A7A4A':'transparent',color:mainTab==='material'?'#fff':'#6B7280'}}>📦 Material</button>
              <button onClick={()=>setMainTab('equipment')} style={{padding:'5px 14px',borderRadius:6,fontSize:12,fontWeight:600,border:'none',cursor:'pointer',fontFamily:'inherit',background:mainTab==='equipment'?'#F97316':'transparent',color:mainTab==='equipment'?'#fff':'#6B7280'}}>🚜 Equipment</button>
              <button onClick={()=>setMainTab('financial')} style={{padding:'5px 14px',borderRadius:6,fontSize:12,fontWeight:600,border:'none',cursor:'pointer',fontFamily:'inherit',background:mainTab==='financial'?'#1D4ED8':'transparent',color:mainTab==='financial'?'#fff':'#6B7280'}}>💰 Financial Summary</button>
            </div>
          <div style={{display:'flex',gap:8}}>
            {mainTab === 'material' ? (
              <>
                {can('Subcontract.Submit') && (
                  <>
                    <button onClick={downloadTemplate} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',cursor:'pointer',fontFamily:'inherit'}}>↓ Template</button>
                    <button onClick={() => setShowUpload(!showUpload)} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',cursor:'pointer',fontFamily:'inherit'}}>↑ Upload</button>
                  </>
                )}
                <button onClick={exportToExcel} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid #E5E7EB',background:'#fff',color:'#6B7280',cursor:'pointer',fontFamily:'inherit'}}>↓ Export</button>
                <button onClick={() => setShowSubReport(true)} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid rgba(29,78,216,.3)',background:'rgba(29,78,216,.06)',color:'#1D4ED8',cursor:'pointer',fontFamily:'inherit'}}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:12,height:12}}><rect x="2" y="2" width="12" height="12" rx="1.5"/><line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="9" y2="9"/></svg>
                  SC Report
                </button>
                {can('Subcontract.Submit') && (
                  <>
                    <button onClick={openConvertModal} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid rgba(26,122,74,.3)',background:'rgba(26,122,74,.08)',color:'#1A7A4A',cursor:'pointer',fontFamily:'inherit'}}>↺ Convert POs</button>
                    <button onClick={openNew} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 14px',borderRadius:6,fontSize:12,fontWeight:600,background:'#1A7A4A',color:'#fff',border:'none',cursor:'pointer',fontFamily:'inherit'}}>+ New Contract</button>
                  </>
                )}
              </>
            ) : mainTab === 'equipment' ? (
              <>
                {can('Subcontract.Submit') && (
                  <>
                    <button onClick={eqOpenConvertModal} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:6,fontSize:12,fontWeight:600,border:'1px solid rgba(249,115,22,.3)',background:'rgba(249,115,22,.08)',color:'#F97316',cursor:'pointer',fontFamily:'inherit'}}>↺ Convert POs</button>
                    <button onClick={eqOpenNew} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 14px',borderRadius:6,fontSize:12,fontWeight:600,background:'#F97316',color:'#fff',border:'none',cursor:'pointer',fontFamily:'inherit'}}>+ New Equipment Contract</button>
                  </>
                )}
              </>
            ) : null}
          </div>
          </div>
        )}
      </div>
    <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:16}}>

      {mainTab === 'equipment' ? (
        <>
          {!eqSelectedContract ? (
            <>
              {/* Equipment KPI Cards */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
                {[
                  { label:'Total Contracts', val:eqStats.total,                       sub:`${eqStats.approved} approved`, color:'#0F1017' },
                  { label:'Total Value',     val:'SAR '+fmt(eqStats.totalValue),       sub:'Contract sum',                 color:'#0F1017' },
                  { label:'Certified',       val:'SAR '+fmt(eqStats.certifiedToDate),  sub:'Cumulative certified',         color:'#1A7A4A' },
                ].map(s => (
                  <div key={s.label} style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 18px'}}>
                    <p style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#6B7280',marginBottom:6}}>{s.label}</p>
                    <p style={{fontSize:20,fontWeight:800,color:s.color,lineHeight:1.1,marginBottom:4}}>{s.val}</p>
                    <p style={{fontSize:11,color:'#9CA3AF'}}>{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* Search + Filter */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <input
                    value={eqSearchQuery}
                    onChange={e => setEqSearchQuery(e.target.value)}
                    placeholder="ابحث بـ رقم العقد، المقاول، PO، Cost Code..."
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                  />
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  {['all', ...STATUSES].map(s => (
                    <button key={s} onClick={() => setEqFilterStatus(s)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${eqFilterStatus === s ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-[#6B7280] border-gray-300 hover:bg-[#FAFAFA]'}`}>
                      {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bulk Action Bar */}
              {eqSelectedIds.size > 0 && (
                <div className="flex items-center gap-3 mb-3 px-4 py-3 bg-[#EDE7DA] border border-orange-200 rounded-xl">
                  <span className="text-sm font-medium text-orange-700">{eqSelectedIds.size} عقد محدد</span>
                  <select value={eqBulkActionType} onChange={e => setEqBulkActionType(e.target.value)}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-1.5">
                    <option value="approve">{can('Subcontract.Submit') ? 'Submit' : 'Approve'}</option>
                    {(can('Subcontract.ApproveCostControl') || can('Subcontract.ApproveDirector')) && <option value="reject">Reject</option>}
                    {can('Subcontract.Submit') && <option value="resubmit">Resubmit</option>}
                    {can('Subcontract.ApproveDirector') && <option value="revoke">Revoke</option>}
                    <option value="delete">Delete</option>
                  </select>
                  <button
                    onClick={() => bulkContractAction(eqBulkActionType, 'equipment')}
                    disabled={bulkApproving}
                    className={`px-4 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 font-medium ${eqBulkActionType==='delete' ? 'bg-red-600 hover:bg-red-700' : eqBulkActionType==='reject' ? 'bg-orange-800 hover:bg-orange-900' : eqBulkActionType==='revoke' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-orange-500 hover:bg-orange-600'}`}>
                    {bulkApproving ? 'جاري...' : `تنفيذ (${eqSelectedIds.size})`}
                  </button>
                  <button onClick={() => setEqSelectedIds(new Set())} className="text-xs text-orange-700/70 hover:text-orange-700 ml-auto">إلغاء التحديد</button>
                </div>
              )}

              {/* Equipment Table */}
              {eqLoading ? (
                <div className="text-center py-12 text-[#6B7280]">Loading...</div>
              ) : eqFiltered.length === 0 ? (
                <div className="text-center py-16 bg-[#EDE7DA] rounded-xl border border-[#E5E7EB]">
                  <div className="text-5xl mb-3">🚜</div>
                  <p className="text-[#6B7280]">لا يوجد عقود معدات.</p>
                </div>
              ) : (
                <div ref={scEqCardRef} className="bg-white rounded-xl border border-[#E5E7EB]" style={{position:'relative'}}>
                  <div ref={scEqScrollRef} onScroll={updateScEqScrollState} style={{overflow:'auto', maxHeight:'70vh', borderRadius:12}}>
                  <table className="text-sm" style={{width:'100%', minWidth:1664}}>
                    <colgroup>{scEqColWidths.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
                    <thead className="bg-[#F5F1EA] border-b border-[#E5E7EB]" style={{position:'sticky', top:0, zIndex:5}}>
                      <tr>
                        <th className="px-4 py-3" style={{background:'#F5F1EA'}}>
                          <input type="checkbox" checked={eqSelectedIds.size===eqFiltered.length && eqFiltered.length>0}
                            onChange={() => setEqSelectedIds(s => s.size===eqFiltered.length ? new Set() : new Set(eqFiltered.map(c=>c.id)))} />
                        </th>
                        <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>PO No</th>
                        <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Contract No</th>
                        <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Contractor</th>
                        <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Cost Code</th>
                        <th className="text-left px-4 py-3 font-medium text-purple-700" style={{background:'#F5F1EA'}}>Resource Code</th>
                        <th className="text-left px-4 py-3 font-medium text-purple-700" style={{background:'#F5F1EA'}}>Resource Description</th>
                        <th className="text-left px-4 py-3 font-medium text-[#1A7A4A]" style={{background:'#F5F1EA'}}>Sub Code</th>
                        <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Scope</th>
                        <th className="text-right px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Value (Exc.VAT)</th>
                        <th className="text-right px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Inc.VAT</th>
                        <th className="text-right px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Certified</th>
                        <th className="text-center px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F5F1EA'}}>Status</th>
                        <th className="px-4 py-3" style={{background:'#F5F1EA'}}></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {eqFiltered.map(c => {
                        const pct = c.contract_value > 0 ? ((c._certified || 0) / c.contract_value * 100).toFixed(1) : 0
                        const sub = eqSubCodesList.find(s => s.code === c.sub_code)
                        return (
                          <tr key={c.id} className="hover:bg-[#FAFAFA]">
                            <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                              <input type="checkbox" checked={eqSelectedIds.has(c.id)}
                                onChange={() => setEqSelectedIds(s => { const n = new Set(s); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n })} />
                            </td>
                            <td className="px-4 py-3 text-xs font-mono text-[#6B7280]">{c.rental_equipment_pos?.po_number || '—'}</td>
                            <td className="px-4 py-3 font-semibold text-[#1C1A18]">{c.contract_no}</td>
                            <td className="px-4 py-3">{c.contractor_name}</td>
                            <td className="px-4 py-3 text-xs font-mono text-[#6B7280]">{sub?.costCode || c.cost_codes?.code || '—'}</td>
                            <td className="px-4 py-3 text-xs font-mono text-purple-700">{sub?.resourceCode || '—'}</td>
                            <td className="px-4 py-3 text-xs text-purple-700">{sub?.resourceCodeDescription || '—'}</td>
                            <td className="px-4 py-3 text-xs font-mono text-[#1A7A4A] font-semibold">{c.sub_code || '—'}</td>
                            <td className="px-4 py-3 text-[#6B7280] max-w-xs truncate">{c.scope}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmt(c.contract_value)}</td>
                            <td className="px-4 py-3 text-right text-[#6B7280]">{fmt(c.contract_inc_vat)}</td>
                            <td className="px-4 py-3 text-right text-[#1A7A4A]">{fmt(c._certified||0)} <span className="text-xs text-[#9CA3AF]">({pct}%)</span></td>
                            <td className="px-4 py-3 text-center">
                              <span title={c.approval_stage==='rejected' && c.rejection_reason ? `سبب الرفض: ${c.rejection_reason}` : undefined}
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${APPROVAL_STAGES[c.approval_stage||'draft']?.bg||'bg-gray-100'} ${APPROVAL_STAGES[c.approval_stage||'draft']?.text||'text-[#6B7280]'}`}>
                                {APPROVAL_STAGES[c.approval_stage||'draft']?.label || c.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 justify-end flex-wrap">
                                {(c.approval_stage === 'draft' || !c.approval_stage) && can('Subcontract.Submit') && (
                                  <button onClick={() => eqHandleApprovalAction(c, 'approve')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#2563EB',color:'#fff',borderRadius:4,cursor:'pointer'}}>Submit</button>
                                )}
                                {c.approval_stage === 'cost_control' && can('Subcontract.ApproveCostControl') && (
                                  <button onClick={() => eqHandleApprovalAction(c, 'approve')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#F97316',color:'#fff',borderRadius:4,cursor:'pointer'}}>✓ Approve</button>
                                )}
                                {c.approval_stage === 'director' && can('Subcontract.ApproveDirector') && (
                                  <button onClick={() => eqHandleApprovalAction(c, 'approve')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#F97316',color:'#fff',borderRadius:4,cursor:'pointer'}}>✓ Final</button>
                                )}
                                {['cost_control','director'].includes(c.approval_stage) && (
                                  <button onClick={() => { setEqRejectTarget(c); setEqRejectReason('') }} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FCA5A5',background:'#FEF2F2',color:'#DC2626',borderRadius:4,cursor:'pointer'}}>✗ Reject</button>
                                )}
                                {c.approval_stage === 'rejected' && can('Subcontract.Submit') && (
                                  <button onClick={() => eqHandleApprovalAction(c, 'resubmit')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#2563EB',color:'#fff',borderRadius:4,cursor:'pointer'}}>↻ Resubmit</button>
                                )}
                                {c.approval_stage === 'approved' && (() => {
                                  // finalApprover mirrors PurchaseOrders.jsx's own
                                  // (dir_approved_by ? 'director' : 'cost_control') logic
                                  // exactly — same rule, same threshold source
                                  // (lib/approvalLimits.js), applied here at the
                                  // contract level per the approved workflow revision.
                                  const finalApprover = c.director_approved_by ? 'director' : 'cost_control'
                                  return (finalApprover === 'director' && can('Subcontract.ApproveDirector')) ||
                                    (finalApprover === 'cost_control' && can('Subcontract.ApproveCostControl'))
                                  // Note: !_hasIpc gate REMOVED here per approved decision —
                                  // Revoke only touches approval fields on the contract row
                                  // itself (approval_stage/cc_approved_by/etc.), never
                                  // ipc_certificates — existing IPCs are entirely unaffected
                                  // by a contract Revoke, so blocking Revoke for having IPCs
                                  // only prevented the exact case Addendums are meant for
                                  // (a contract already has certified work and needs a
                                  // scope/value change). The delete-contract guards elsewhere
                                  // in this file (which DO need this gate, since deleting a
                                  // contract with certified IPCs is a real data-integrity
                                  // risk) are unchanged.
                                })() && (
                                  <button onClick={() => { if(window.confirm('Revoke approval and return this contract to Draft? An Addendum can then be created as a Purchase Order.')) eqHandleApprovalAction(c, 'revoke') }} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FCD34D',background:'#FFFBEB',color:'#92400E',borderRadius:4,cursor:'pointer'}}>↺ Revoke</button>
                                )}
                                {(c.approval_stage || 'draft') === 'draft' && c.po_id && can('PO.Submit') && (
                                  // Addendum creation moved entirely to
                                  // PurchaseOrders.jsx — this is a Navigation
                                  // link only, not a form. The actual "is this
                                  // PO eligible for an Addendum" check happens
                                  // in PurchaseOrders.jsx itself (via
                                  // repContractPoIds — a real linked-contract
                                  // check, immune to the same "cc_approved_by
                                  // clears on revoke" ambiguity this page
                                  // can't resolve on its own). Showing this
                                  // link a bit more liberally than strictly
                                  // necessary is safe: worst case, Procurement
                                  // clicks through and finds no "+ Addendum"
                                  // button on that PO in the Purchase Orders
                                  // list.
                                  <button onClick={() => { if (onNavigate) onNavigate('purchase-orders') }} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FDBA74',background:'#FFF7ED',color:'#C2410C',borderRadius:4,cursor:'pointer'}}>+ Addendum (in Purchase Orders)</button>
                                )}
                                <button onClick={() => eqOpenContractDetail(c)} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid rgba(249,115,22,.3)',background:'rgba(249,115,22,.08)',color:'#F97316',borderRadius:4,cursor:'pointer'}}>Detail</button>
                                <button onClick={() => {
                                  if (c.approval_stage !== 'approved') { alert('لا يمكن إنشاء مستخلص (IPC) إلا بعد اعتماد العقد بالكامل.'); return }
                                  localStorage.setItem('costspace_open_ipc_for_contract', JSON.stringify({ contractId: c.id, ts: Date.now() }))
                                  if (onNavigate) onNavigate('ipc')
                                }}
                                  disabled={c.approval_stage !== 'approved'}
                                  title={c.approval_stage !== 'approved' ? 'العقد لسه مش approved' : ''}
                                  style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #E5E7EB',background: c.approval_stage !== 'approved' ? '#F3F4F6' : '#fff',color: c.approval_stage !== 'approved' ? '#D1D5DB' : '#6B7280',borderRadius:4,cursor: c.approval_stage !== 'approved' ? 'not-allowed' : 'pointer'}}>+ IPC</button>
                                <button onClick={() => eqOpenEdit(c)} className="text-[#9CA3AF] hover:text-[#6B7280] text-base">✏</button>
                                <DocButton entityId={c.id} entityType="equipment_contract" projectId={c.project_id}/>
                                {can('Subcontract.Submit') && (
                                  <button onClick={() => eqDeleteContract(c.id, c._hasIpc)} className="text-[#9CA3AF] hover:text-red-500 text-base">🗑</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]">
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-sm font-medium text-[#6B7280]">Total ({eqFiltered.length})</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(eqFiltered.reduce((s, c) => s + (parseFloat(c.contract_value) || 0), 0))}</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(eqFiltered.reduce((s, c) => s + (parseFloat(c.contract_inc_vat) || 0), 0))}</td>
                        <td className="px-4 py-3 text-right font-bold">{fmt(eqFiltered.reduce((s, c) => s + (c._certified || 0), 0))}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                  {scEqScrollState.hasOverflow && (
                    <div style={{
                      position:'fixed', bottom:0, left:scEqNavLeftRight.left, right:scEqNavLeftRight.right, zIndex:50,
                      display:'flex', alignItems:'center', gap:8,
                      padding:'6px 12px', background:'rgba(255,255,255,0.97)',
                      borderTop:'1px solid #E5E7EB', backdropFilter:'blur(2px)',
                    }}>
                      <button onClick={()=>scEqScrollBy(-1)} disabled={scEqScrollState.atStart} aria-label="Scroll table left"
                        style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:scEqScrollState.atStart?'#F9FAFB':'#fff',color:scEqScrollState.atStart?'#D1D5DB':'#6B7280',cursor:scEqScrollState.atStart?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>‹</button>
                      <input type="range" min={0} max={1000} step={1} value={Math.round(scEqScrollState.ratio*1000)}
                        onChange={e=>scEqScrollToRatio(Number(e.target.value)/1000)} aria-label="Scroll table horizontally"
                        style={{flex:1,height:4,accentColor:'#1B3A6B',cursor:'pointer'}}/>
                      <button onClick={()=>scEqScrollBy(1)} disabled={scEqScrollState.atEnd} aria-label="Scroll table right"
                        style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:scEqScrollState.atEnd?'#F9FAFB':'#fff',color:scEqScrollState.atEnd?'#D1D5DB':'#6B7280',cursor:scEqScrollState.atEnd?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>›</button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Equipment Contract Detail */
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold">{eqSelectedContract.contract_no}</h2>
                  <p className="text-sm text-[#6B7280]">{eqSelectedContract.contractor_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${APPROVAL_STAGES[eqSelectedContract.approval_stage||'draft']?.bg} ${APPROVAL_STAGES[eqSelectedContract.approval_stage||'draft']?.text}`}>
                    {APPROVAL_STAGES[eqSelectedContract.approval_stage||'draft']?.label}
                  </span>
                  <button onClick={() => setEqSelectedContract(null)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg">← Back</button>
                </div>
              </div>
              {eqSelectedContract.approval_stage === 'rejected' && eqSelectedContract.rejection_reason && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <b>سبب الرفض:</b> {eqSelectedContract.rejection_reason}
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
                <div><span className="text-[#6B7280]">PO:</span> <b>{eqSelectedContract.rental_equipment_pos?.po_number || '—'}</b></div>
                <div><span className="text-[#6B7280]">Cost Code:</span> <b>{eqSelectedContract.cost_codes?.code || '—'}</b></div>
                <div><span className="text-[#6B7280]">Sub Code:</span> <b>{eqSelectedContract.sub_code || '—'}</b></div>
                {(() => { const sub = eqSubCodesList.find(s => s.code === eqSelectedContract.sub_code); return sub?.resourceCode ? (
                  <div><span className="text-[#6B7280]">Resource Code:</span> <b className="text-purple-700">{sub.resourceCode}</b></div>
                ) : null })()}
                <div><span className="text-[#6B7280]">Contract Value:</span> <b>{fmt(eqSelectedContract.contract_value)}</b></div>
                <div><span className="text-[#6B7280]">VAT %:</span> <b>{eqSelectedContract.vat_pct}%</b></div>
                <div><span className="text-[#6B7280]">Retention %:</span> <b>{eqSelectedContract.retention_pct}%</b></div>
                <div><span className="text-[#6B7280]">Advance %:</span> <b>{eqSelectedContract.advance_pct}%</b></div>
                <div><span className="text-[#6B7280]">Adv Recovery Rate:</span> <b>{eqSelectedContract.adv_recovery_rate||0}%</b></div>
                <div><span className="text-[#6B7280]">VAT Treatment:</span> <b>{(eqSelectedContract.vat_case||1)===2 ? 'Case 2 (VAT on Adv)' : 'Case 1 (Standard)'}</b></div>
              </div>
              {!['approved', 'rejected'].includes(eqSelectedContract.approval_stage) && (
                <div className="flex gap-2 mb-4">
                  {(eqSelectedContract.approval_stage === 'draft' || !eqSelectedContract.approval_stage) && can('Subcontract.Submit') && (
                    <button onClick={() => eqHandleApprovalAction(eqSelectedContract, 'approve')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Submit for Cost Control</button>
                  )}
                  {eqSelectedContract.approval_stage === 'cost_control' && can('Subcontract.ApproveCostControl') && (
                    <button onClick={() => eqHandleApprovalAction(eqSelectedContract, 'approve')} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm">✓ Approve</button>
                  )}
                  {eqSelectedContract.approval_stage === 'director' && can('Subcontract.ApproveDirector') && (
                    <button onClick={() => eqHandleApprovalAction(eqSelectedContract, 'approve')} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm">✓ Final Approve</button>
                  )}
                  {['cost_control','director'].includes(eqSelectedContract.approval_stage) && (
                    <button onClick={() => eqHandleApprovalAction(eqSelectedContract, 'reject')} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm">✗ Reject</button>
                  )}
                </div>
              )}
              {eqSelectedContract.approval_stage === 'rejected' && can('Subcontract.Submit') && (
                <div className="flex gap-2 mb-4">
                  <button onClick={() => eqHandleApprovalAction(eqSelectedContract, 'resubmit')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">↻ Resubmit</button>
                </div>
              )}
              {eqSelectedContract.approval_stage === 'approved' && (() => {
                const finalApprover = eqSelectedContract.director_approved_by ? 'director' : 'cost_control'
                return (finalApprover === 'director' && can('Subcontract.ApproveDirector')) ||
                  (finalApprover === 'cost_control' && can('Subcontract.ApproveCostControl'))
              })() && (
                <div className="flex gap-2 mb-4">
                  <button onClick={() => { if(window.confirm('Revoke approval and return this contract to Draft? An Addendum can then be created as a Purchase Order.')) eqHandleApprovalAction(eqSelectedContract, 'revoke') }} className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm">↺ Revoke Approval</button>
                </div>
              )}
              {(eqSelectedContract.approval_stage || 'draft') === 'draft' && eqSelectedContract.po_id && can('PO.Submit') && (
                <div className="flex gap-2 mb-4">
                  <button onClick={() => { if (onNavigate) onNavigate('purchase-orders') }} className="px-4 py-2 bg-orange-50 border border-orange-300 text-orange-700 rounded-lg text-sm">+ Addendum (in Purchase Orders)</button>
                </div>
              )}

              {/* Addendum History */}
              <AddendumHistorySection contractId={eqSelectedContract.id} />

              {/* Audit Trail */}
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-6">
                <h3 className="font-semibold text-[#2E2C29] mb-3">Audit Trail</h3>
                {auditHistory.length === 0 ? (
                  <p className="text-xs text-[#9CA3AF]">لا يوجد سجل حتى الآن.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {auditHistory.map(a => (
                      <div key={a.id} className="flex items-start gap-3 text-xs border-b border-[#F3F4F6] pb-2 last:border-0">
                        <span className="text-[#9CA3AF] whitespace-nowrap font-mono">{new Date(a.created_at).toLocaleString('en-GB', {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
                        <span className="font-semibold text-[#2E2C29]">{a.actor_name || '—'}</span>
                        <span className="text-[#6B7280]">
                          {a.entity_type === 'purchase_order' || a.entity_type === 'rental_equipment_po' ? '[PO] ' : '[Contract] '}
                          {a.action}
                          {a.previous_status && a.new_status ? ` (${a.previous_status} → ${a.new_status})` : ''}
                        </span>
                        {a.comment && <span className="text-[#9CA3AF] italic">— {a.comment}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">PO Line Items</h3>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F9FAFB]">
                      <th className="text-left px-2 py-1.5">Sub Code</th>
                      <th className="text-left px-2 py-1.5">Sub Code Description</th>
                      <th className="text-left px-2 py-1.5">Equipment Description</th>
                      <th className="text-left px-2 py-1.5">Cost Code</th>
                      <th className="text-left px-2 py-1.5">Cost Code Description</th>
                      <th className="text-left px-2 py-1.5 text-purple-700">Resource Code</th>
                      <th className="text-left px-2 py-1.5 text-purple-700">Resource Description</th>
                      <th className="text-right px-2 py-1.5">Qty</th>
                      <th className="text-left px-2 py-1.5">Unit</th>
                      <th className="text-right px-2 py-1.5">Rate</th>
                      <th className="text-right px-2 py-1.5">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eqPoLines.map(l => {
                      const subInfo = eqSubCodesList.find(s => s.code === l.equipment_type)
                      return (
                      <tr key={l.id} className="border-b border-[#F3F4F6]">
                        <td className="px-2 py-1.5 font-mono text-[#2563EB] font-semibold">{l.equipment_type}</td>
                        <td className="px-2 py-1.5 text-[#6B7280]">{eqSubCodeDescMap[l.equipment_type] || '—'}</td>
                        <td className="px-2 py-1.5 text-[#6B7280]">
                          {l.description || '—'}
                          {l.addendum_no && (
                            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-orange-100 text-orange-700">Addendum #{l.addendum_no}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[#6B7280]">{l.cost_codes?.code || '—'}</td>
                        <td className="px-2 py-1.5 text-[#6B7280]">{l.cost_codes?.description || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-purple-700">{subInfo?.resourceCode || '—'}</td>
                        <td className="px-2 py-1.5 text-purple-700">{subInfo?.resourceCodeDescription || '—'}</td>
                        <td className="px-2 py-1.5 text-right">{l.quantity}</td>
                        <td className="px-2 py-1.5">{l.unit}</td>
                        <td className="px-2 py-1.5 text-right">{l.rate}</td>
                        <td className="px-2 py-1.5 text-right font-semibold">{fmt(l.total_cost)}</td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : mainTab === 'financial' ? (
        <>
          <div style={{marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <input value={finSearch} onChange={e=>setFinSearch(e.target.value)}
              placeholder="🔍 بحث برقم العقد أو اسم المقاول..."
              style={{width:320,maxWidth:'100%',padding:'8px 12px',borderRadius:8,border:'1px solid #E5E7EB',fontSize:13,fontFamily:'inherit'}} />
          </div>
          {finLoading ? (
            <div style={{padding:60,textAlign:'center',color:'#9CA3AF'}}>Loading…</div>
          ) : finSummary.length === 0 ? (
            <div style={{padding:60,textAlign:'center',color:'#9CA3AF'}}>لا يوجد بيانات مالية بعد — لسه مفيش مستخلصات معتمدة.</div>
          ) : (() => {
            const finFiltered = finSummary.filter(r =>
              !finSearch.trim() ||
              r.contract_no?.toLowerCase().includes(finSearch.trim().toLowerCase()) ||
              r.contractor_name?.toLowerCase().includes(finSearch.trim().toLowerCase())
            )
            return finFiltered.length === 0 ? (
              <div style={{padding:60,textAlign:'center',color:'#9CA3AF'}}>لا يوجد نتائج مطابقة لـ "{finSearch}"</div>
            ) : (
            <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
              {/* Print Financial Statement controls — user must clearly
                  see which contractors are selected before printing
                  (approved spec, item 13). Reads/writes the SAME
                  finSummary rows already on screen — no second
                  contractor source. */}
              <div style={{padding:'10px 14px',display:'flex',alignItems:'center',gap:10,borderBottom:'1px solid #E5E7EB',background:'#FAFAFA',flexWrap:'wrap'}}>
                <button onClick={()=>selectAllFinRows(finFiltered)} style={{padding:'6px 12px',fontSize:11.5,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Select All</button>
                <button onClick={clearFinSelection} style={{padding:'6px 12px',fontSize:11.5,borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Clear</button>
                <span style={{fontSize:11.5,color:'#6B7280'}}>
                  {finSelectedIds.size === 0 ? 'No contractor selected' : `${finSelectedIds.size} contractor${finSelectedIds.size>1?'s':''} selected`}
                </span>
                <button onClick={handlePrintFinancialStatement} disabled={finSelectedIds.size===0 || finPrinting}
                  style={{marginLeft:'auto',padding:'7px 16px',fontSize:12,fontWeight:700,borderRadius:6,border:'none',background:finSelectedIds.size===0?'#E5E7EB':'#1A7A4A',color:finSelectedIds.size===0?'#9CA3AF':'#fff',cursor:finSelectedIds.size===0||finPrinting?'not-allowed':'pointer'}}>
                  {finPrinting ? 'Preparing…' : finSelectedIds.size === 1 ? '🖨 Print Financial Statement' : '🖨 Print Financial Summary'}
                </button>
              </div>
              <div style={{overflow:'auto', maxHeight:'70vh'}}>
              <table style={{width:'100%',fontSize:13,borderCollapse:'collapse'}}>
                <thead style={{position:'sticky',top:0,zIndex:1}}>
                  <tr style={{background:'#F9FAFB',borderBottom:'1px solid #E5E7EB'}}>
                    <th style={{padding:'10px 8px',width:30}}>
                      <input type="checkbox" checked={finFiltered.length>0 && finFiltered.every(r=>finSelectedIds.has(r.id))} onChange={()=> finFiltered.every(r=>finSelectedIds.has(r.id)) ? clearFinSelection() : selectAllFinRows(finFiltered)} />
                    </th>
                    <th style={{textAlign:'left',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>Contract</th>
                    <th style={{textAlign:'left',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>Contractor</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>Contract Value</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>Cumulative Certified</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>Total Retention</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1A7A4A'}}>Released</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#DC2626'}}>Retention Held</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1D4ED8'}}>Total Paid</th>
                    <th style={{textAlign:'right',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1A7A4A'}}>Outstanding</th>
                    <th style={{textAlign:'center',padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}></th>

                  </tr>
                </thead>
                <tbody>
                  {finFiltered.map((r, idx) => (
                    <tr key={r.id} style={{background:idx%2===0?'#fff':'#FAFAFA',borderBottom:'1px solid #F3F4F6'}}>
                      <td style={{padding:'10px 8px',textAlign:'center'}}>
                        <input type="checkbox" checked={finSelectedIds.has(r.id)} onChange={()=>toggleFinSelection(r.id)} />
                      </td>
                      <td style={{padding:'10px 14px',fontFamily:'monospace',fontSize:12}}>
                        <span title={r.contract_type==='equipment'?'Equipment Contract':'Material Contract'}>{r.contract_type==='equipment'?'🚜':'📦'}</span> {r.contract_no}
                      </td>
                      <td style={{padding:'10px 14px',fontWeight:600}}>{r.contractor_name}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.contract_value)}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.cumulative_certified)}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums',color:'#6B7280'}}>{fmt(r.retention_total)}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums',color:'#1A7A4A'}}>{r.retention_released>0?fmt(r.retention_released):'—'}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums',color:'#DC2626',fontWeight:600}}>{fmt(r.retention_held)}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums',color:'#1D4ED8',fontWeight:600}}>{fmt(r.total_paid)}</td>
                      <td style={{padding:'10px 14px',textAlign:'right',fontVariantNumeric:'tabular-nums',fontWeight:700,color:r.outstanding>0?'#DC2626':r.outstanding<0?'#D97706':'#1A7A4A'}}>
                        {r.outstanding>0 ? fmt(r.outstanding) : r.outstanding<0 ? `(${fmt(Math.abs(r.outstanding))})` : '—'}
                      </td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <button onClick={() => openStatement(r)} style={{fontSize:11,fontWeight:600,padding:'4px 10px',borderRadius:6,border:'1px solid #1D4ED8',background:'rgba(29,78,216,.06)',color:'#1D4ED8',cursor:'pointer',fontFamily:'inherit'}}>📋 Statement</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:'#F9FAFB',borderTop:'2px solid #E5E7EB',fontWeight:700}}>
                    <td colSpan={2} style={{padding:'10px 14px'}}>Total</td>
                    <td style={{padding:'10px 14px',textAlign:'right'}}>{fmt(finFiltered.reduce((s,r)=>s+r.contract_value,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right'}}>{fmt(finFiltered.reduce((s,r)=>s+r.cumulative_certified,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'#6B7280'}}>{fmt(finFiltered.reduce((s,r)=>s+r.retention_total,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'#1A7A4A'}}>{fmt(finFiltered.reduce((s,r)=>s+r.retention_released,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'#DC2626'}}>{fmt(finFiltered.reduce((s,r)=>s+r.retention_held,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'#1D4ED8'}}>{fmt(finFiltered.reduce((s,r)=>s+r.total_paid,0))}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'#1A7A4A'}}>{fmt(finFiltered.reduce((s,r)=>s+r.outstanding,0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              </div>
            </div>
            )
          })()}
        </>
      ) : (
      <>

      {!selectedContract ? (
        <>
          {/* Upload */}
          {showUpload && (
            <div className="bg-[#EDE7DA] border border-[#1A7A4A]/30 rounded-xl p-5 mb-5">
              <h3 className="font-semibold text-[#1C1A18] mb-2">Upload Contracts from Excel</h3>
              <p className="text-xs text-[#1A7A4A] mb-3">الأعمدة الإلزامية: <strong>contract_no, contractor_name</strong></p>
              <div className="flex items-center gap-3 mb-3">
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload}
                  className="text-xs text-[#6B7280] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1A7A4A] file:text-white file:text-sm" />
              </div>
              {uploadError && <p className="text-red-500 text-sm mb-2">{uploadError}</p>}
              {uploadRows.length > 0 && (
                <div>
                  <p className="text-sm text-[#1A7A4A] mb-2">تم قراءة <strong>{uploadRows.length}</strong> عقد</p>
                  <button onClick={confirmUpload} className="px-4 py-2 bg-[#1A7A4A] text-white text-sm rounded-lg hover:bg-[#156637]">
                    ✓ تأكيد الرفع ({uploadRows.length} عقد)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* KPI Cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
            {[
              { label:'Total Contracts', val:stats.total,                       sub:`${stats.approved} approved`,     color:'#0F1017' },
              { label:'Total Value',     val:'SAR '+fmt(stats.totalValue),       sub:'Contract sum',                   color:'#0F1017' },
              { label:'Certified',       val:'SAR '+fmt(stats.certifiedToDate),  sub:'Cumulative certified',           color:'#1A7A4A' },
              { label:'Retention Held',  val:(finLoading && finSummary.length === 0) ? '…' : 'SAR '+fmt(finSummary.filter(r => r.contract_type === 'material').reduce((sum, r) => sum + (r.retention_held || 0), 0)), sub:'Net of releases', color:'#D97706' },
            ].map(s => (
              <div key={s.label} style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 18px'}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#6B7280',marginBottom:6}}>{s.label}</p>
                <p style={{fontSize:20,fontWeight:800,color:s.color,lineHeight:1.1,marginBottom:4}}>{s.val}</p>
                <p style={{fontSize:11,color:'#9CA3AF'}}>{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
              </svg>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="ابحث بـ رقم العقد، المقاول، PO، Cost Code، Scope..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A7A4A] focus:border-blue-500 bg-white"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] text-lg leading-none">×</button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {/* Logistics filter */}
              <select
                value={filterLogistics}
                onChange={e => setFilterLogistics(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#1A7A4A] focus:border-blue-500">
                {logisticsOptions.map(l => (
                  <option key={l} value={l}>
                    {l === 'all' ? '🚚 All Types' :
                     l === 'oceanarium' ? 'Ocn' :
                     l === 'logistics'  ? 'Log' : l}
                  </option>
                ))}
              </select>
              {/* Status filter */}
              {['all', ...STATUSES].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${filterStatus === s ? 'bg-[#1A7A4A] text-white border-blue-600' : 'bg-white text-[#6B7280] border-gray-300 hover:bg-[#FAFAFA]'}`}>
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mb-3 px-4 py-3 bg-[#EDE7DA] border border-indigo-200 rounded-xl">
              <span className="text-sm font-medium text-[#1A7A4A]">{selectedIds.size} عقد محدد</span>
              <select value={bulkActionType} onChange={e => setBulkActionType(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5">
                <option value="approve">{can('Subcontract.Submit') ? 'Submit' : 'Approve'}</option>
                {(can('Subcontract.ApproveCostControl') || can('Subcontract.ApproveDirector')) && <option value="reject">Reject</option>}
                {can('Subcontract.Submit') && <option value="resubmit">Resubmit</option>}
                {can('Subcontract.ApproveDirector') && <option value="revoke">Revoke</option>}
                <option value="delete">Delete</option>
              </select>
              <button
                onClick={() => bulkContractAction(bulkActionType, 'material')}
                disabled={bulkApproving}
                className={`px-4 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 font-medium flex items-center gap-1.5 ${bulkActionType==='delete' ? 'bg-red-600 hover:bg-red-700' : bulkActionType==='reject' ? 'bg-orange-600 hover:bg-orange-700' : bulkActionType==='revoke' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}>
                {bulkApproving ? (
                  <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> جاري...</>
                ) : `تنفيذ (${selectedIds.size})`}
              </button>
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-[#1A7A4A]/70 hover:text-[#1A7A4A] ml-auto">إلغاء التحديد</button>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="text-center py-12 text-[#6B7280]">Loading...</div>
          ) : !projectId ? (
            <div className="text-center py-12 text-[#9CA3AF]">Select a project first</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-[#EDE7DA] rounded-xl border border-[#E5E7EB]">
              <div className="text-5xl mb-3">📝</div>
              <p className="text-[#6B7280]">لا يوجد عقود.</p>
            </div>
          ) : (
            <div ref={scMatCardRef} className="bg-white rounded-xl border border-[#E5E7EB]" style={{position:'relative'}}>
              <div ref={scMatScrollRef} onScroll={updateScMatScrollState} style={{overflow:'auto', maxHeight:'70vh', borderRadius:12}}>
              <table className="text-sm" style={{width:'100%', minWidth:1754}}>
                <colgroup>{scMatColWidths.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]" style={{position:'sticky', top:0, zIndex:5}}>
                  <tr>
                    <th className="px-4 py-3 w-10" style={{background:'#F9FAFB'}}>
                      <input type="checkbox"
                        checked={filtered.length > 0 && filtered.every(c => selectedIds.has(c.id))}
                        onChange={e => {
                          if (e.target.checked) setSelectedIds(new Set(filtered.map(c => c.id)))
                          else setSelectedIds(new Set())
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-[#1A7A4A] cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>PO No</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Contract No</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Contractor</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Cost Code</th>
                    <th className="text-left px-4 py-3 font-medium text-purple-700" style={{background:'#F9FAFB'}}>Resource Code</th>
                    <th className="text-left px-4 py-3 font-medium text-purple-700" style={{background:'#F9FAFB'}}>Resource Description</th>
                    <th className="text-left px-4 py-3 font-medium text-[#1A7A4A]" style={{background:'#F9FAFB'}}>Sub Code</th>
                    <th className="text-center px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Type</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Scope</th>
                    <th className="text-right px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Value (Exc.VAT)</th>
                    <th className="text-right px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Inc. VAT</th>
                    <th className="text-right px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Certified</th>
                    <th className="text-center px-4 py-3 font-medium text-[#6B7280]" style={{background:'#F9FAFB'}}>Status</th>
                    <th className="px-4 py-3" style={{background:'#F9FAFB'}}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                  {filtered.map(c => {
                    const pct = c.contract_value > 0 ? ((c._certified || 0) / c.contract_value * 100).toFixed(1) : 0
                    const sub = subCodesList.find(s => s.code === c.sub_code)
                    return (
                      <tr key={c.id} className={`hover:bg-[#FAFAFA] ${selectedIds.has(c.id) ? 'bg-[#EDE7DA]/50' : ''}`}>
                        <td className="px-4 py-3 w-10">
                          <input type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={e => {
                              const next = new Set(selectedIds)
                              e.target.checked ? next.add(c.id) : next.delete(c.id)
                              setSelectedIds(next)
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-[#1A7A4A] cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{c.purchase_orders?.po_number || '—'}</td>
                        <td className="px-4 py-3 font-mono text-[#1A7A4A] font-medium cursor-pointer hover:underline"
                            onClick={() => openContractDetail(c)}>
                          {c.contract_no}
                          {c.purchase_orders?.po_number && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-normal bg-[#DDD9D0] text-[#1A7A4A] border border-indigo-200">
                              PO
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium">{c.contractor_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{sub?.costCode || c.cost_codes?.code || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-purple-700">{sub?.resourceCode || '—'}</td>
                        <td className="px-4 py-3 text-xs text-purple-700">{sub?.resourceCodeDescription || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A] font-semibold">{c.sub_code || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          {c.logistics_type ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              c.logistics_type === 'oceanarium' ? 'bg-[#DDD9D0] text-[#1A7A4A]' :
                              c.logistics_type === 'logistics'  ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-[#6B7280]'
                            }`}>
                              {c.logistics_type === 'oceanarium' ? 'Ocn' :
                               c.logistics_type === 'logistics'  ? 'Log' :
                               c.logistics_type}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-[#6B7280] max-w-xs truncate">{c.scope}</td>
                        <td className="px-4 py-3 text-right font-medium">{fmt(c.contract_value)}</td>
                        <td className="px-4 py-3 text-right text-[#6B7280]">{fmt(c.contract_inc_vat)}</td>
                        <td className="px-4 py-3">
                          <div style={{fontWeight:600,color:'#1A7A4A',fontSize:12,marginBottom:3}}>{fmt(c._certified || 0)}</div>
                          <div style={{height:4,background:'#E5E7EB',borderRadius:2,width:80,overflow:'hidden',marginBottom:2}}>
                            <div style={{height:'100%',width:`${Math.min(parseFloat(pct),100)}%`,background:parseFloat(pct)>80?'#1A7A4A':parseFloat(pct)>40?'#D97706':'#9CA3AF',borderRadius:2}}/>
                          </div>
                          <div style={{fontSize:10,color:'#9CA3AF'}}>{pct}%</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span title={c.approval_stage==='rejected' && c.rejection_reason ? `سبب الرفض: ${c.rejection_reason}` : undefined}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${APPROVAL_STAGES[c.approval_stage||'draft']?.bg||'bg-gray-100'} ${APPROVAL_STAGES[c.approval_stage||'draft']?.text||'text-[#6B7280]'}`}>
                            {APPROVAL_STAGES[c.approval_stage||'draft']?.label || c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                            {(c.approval_stage === 'draft' || !c.approval_stage) && can('Subcontract.Submit') && (
                              <button onClick={() => handleApprovalAction(c, 'approve')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#2563EB',color:'#fff',borderRadius:4,cursor:'pointer'}}>Submit</button>
                            )}
                            {c.approval_stage === 'cost_control' && can('Subcontract.ApproveCostControl') && (
                              <button onClick={() => handleApprovalAction(c, 'approve')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#1A7A4A',color:'#fff',borderRadius:4,cursor:'pointer'}}>✓ Approve</button>
                            )}
                            {c.approval_stage === 'director' && can('Subcontract.ApproveDirector') && (
                              <button onClick={() => handleApprovalAction(c, 'approve')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#1A7A4A',color:'#fff',borderRadius:4,cursor:'pointer'}}>✓ Final</button>
                            )}
                            {['cost_control','director'].includes(c.approval_stage) && (
                              <button onClick={() => { setRejectTarget(c); setRejectReason('') }} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FCA5A5',background:'#FEF2F2',color:'#DC2626',borderRadius:4,cursor:'pointer'}}>✗ Reject</button>
                            )}
                            {c.approval_stage === 'rejected' && can('Subcontract.Submit') && (
                              <button onClick={() => handleApprovalAction(c, 'resubmit')} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'none',background:'#2563EB',color:'#fff',borderRadius:4,cursor:'pointer'}}>↻ Resubmit</button>
                            )}
                            {c.approval_stage === 'approved' && (() => {
                              const finalApprover = c.director_approved_by ? 'director' : 'cost_control'
                              return (finalApprover === 'director' && can('Subcontract.ApproveDirector')) ||
                                (finalApprover === 'cost_control' && can('Subcontract.ApproveCostControl'))
                            })() && (
                              <button onClick={() => { if(window.confirm('Revoke approval and return this contract to Draft? An Addendum can then be created as a Purchase Order.')) handleApprovalAction(c, 'revoke') }} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FCD34D',background:'#FFFBEB',color:'#92400E',borderRadius:4,cursor:'pointer'}}>↺ Revoke</button>
                            )}
                            {(c.approval_stage || 'draft') === 'draft' && c.po_id && can('PO.Submit') && (
                              <button onClick={() => { if (onNavigate) onNavigate('purchase-orders') }} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #FDBA74',background:'#FFF7ED',color:'#C2410C',borderRadius:4,cursor:'pointer'}}>+ Addendum (in Purchase Orders)</button>
                            )}
                            <button onClick={() => openContractDetail(c)} style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid rgba(26,122,74,.3)',background:'rgba(26,122,74,.08)',color:'#1A7A4A',borderRadius:4,cursor:'pointer'}}>Detail</button>
                            <button onClick={() => {
                              if (c.approval_stage !== 'approved') { alert('لا يمكن إنشاء مستخلص (IPC) إلا بعد اعتماد العقد بالكامل.'); return }
                              localStorage.setItem('costspace_open_ipc_for_contract', JSON.stringify({ contractId: c.id, ts: Date.now() }))
                              if (onNavigate) onNavigate('ipc')
                            }}
                              disabled={c.approval_stage !== 'approved'}
                              title={c.approval_stage !== 'approved' ? 'العقد لسه مش approved' : ''}
                              style={{padding:'3px 8px',fontSize:10.5,fontWeight:600,border:'1px solid #E5E7EB',background: c.approval_stage !== 'approved' ? '#F3F4F6' : '#fff',color: c.approval_stage !== 'approved' ? '#D1D5DB' : '#6B7280',borderRadius:4,cursor: c.approval_stage !== 'approved' ? 'not-allowed' : 'pointer'}}>+ IPC</button>
                            <button onClick={() => openEdit(c)} className="text-[#9CA3AF] hover:text-[#6B7280] text-base">✏</button>
                            <DocButton entityId={c.id} entityType="subcontract" projectId={c.project_id}/>
                            {can('Subcontract.Submit') && (
                              <button onClick={() => deleteContract(c.id, c._hasIpc)} className="text-[#9CA3AF] hover:text-red-500 text-base">🗑</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]">
                  <tr>
                    <td colSpan={7} className="px-4 py-3 text-sm font-medium text-[#6B7280]">Total ({filtered.length})</td>
                    <td className="px-4 py-3 text-right font-bold">{fmt(filtered.reduce((s, c) => s + (parseFloat(c.contract_value) || 0), 0))}</td>
                    <td className="px-4 py-3 text-right font-bold">{fmt(filtered.reduce((s, c) => s + (parseFloat(c.contract_inc_vat) || 0), 0))}</td>
                    <td className="px-4 py-3 text-right font-bold">{fmt(filtered.reduce((s, c) => s + (c._certified || 0), 0))}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
              </div>
              {scMatScrollState.hasOverflow && (
                <div style={{
                  position:'fixed', bottom:0, left:scMatNavLeftRight.left, right:scMatNavLeftRight.right, zIndex:50,
                  display:'flex', alignItems:'center', gap:8,
                  padding:'6px 12px', background:'rgba(255,255,255,0.97)',
                  borderTop:'1px solid #E5E7EB', backdropFilter:'blur(2px)',
                }}>
                  <button onClick={()=>scMatScrollBy(-1)} disabled={scMatScrollState.atStart} aria-label="Scroll table left"
                    style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:scMatScrollState.atStart?'#F9FAFB':'#fff',color:scMatScrollState.atStart?'#D1D5DB':'#6B7280',cursor:scMatScrollState.atStart?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>‹</button>
                  <input type="range" min={0} max={1000} step={1} value={Math.round(scMatScrollState.ratio*1000)}
                    onChange={e=>scMatScrollToRatio(Number(e.target.value)/1000)} aria-label="Scroll table horizontally"
                    style={{flex:1,height:4,accentColor:'#1B3A6B',cursor:'pointer'}}/>
                  <button onClick={()=>scMatScrollBy(1)} disabled={scMatScrollState.atEnd} aria-label="Scroll table right"
                    style={{width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:scMatScrollState.atEnd?'#F9FAFB':'#fff',color:scMatScrollState.atEnd?'#D1D5DB':'#6B7280',cursor:scMatScrollState.atEnd?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>›</button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* ── CONTRACT DETAIL ── */
        <div>
          {/* Approval Workflow */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#2E2C29]">Approval Workflow</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setPrintContractId(selectedContract.id)}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-[#1B3A6B] text-white hover:bg-[#152d54]">
                  📄 Contract Document
                </button>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${APPROVAL_STAGES[selectedContract.approval_stage||'draft']?.bg} ${APPROVAL_STAGES[selectedContract.approval_stage||'draft']?.text}`}>
                  {APPROVAL_STAGES[selectedContract.approval_stage||'draft']?.label}
                </span>
              </div>
            </div>

            {/* Trail */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { key: 'cm',       label: 'Contract Manager', by: selectedContract.cm_approved_by,       at: selectedContract.cm_approved_at },
                { key: 'cc',       label: 'Cost Control',     by: selectedContract.cc_approved_by,       at: selectedContract.cc_approved_at },
                { key: 'director', label: 'Director',         by: selectedContract.director_approved_by, at: selectedContract.director_approved_at },
              ].map(step => (
                <div key={step.key} className={`rounded-lg border p-3 ${step.by ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-[#E5E7EB]'}`}>
                  <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{step.label}</p>
                  {step.by ? (
                    <>
                      <p className="text-xs font-semibold text-green-700">✓ {step.by}</p>
                      <p className="text-[10px] text-[#9CA3AF]">{new Date(step.at).toLocaleDateString('en-GB')}</p>
                    </>
                  ) : (
                    <p className="text-xs text-[#9CA3AF]">Pending</p>
                  )}
                </div>
              ))}
            </div>

            {selectedContract.approval_stage === 'rejected' && selectedContract.rejection_reason && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                <span className="font-semibold">Rejection reason: </span>{selectedContract.rejection_reason}
              </div>
            )}

            {/* Action buttons */}
            {!['approved', 'rejected'].includes(selectedContract.approval_stage) && (
              <div className="flex items-center gap-3 pt-3 border-t border-[#F3F4F6]">
                {(selectedContract.approval_stage === 'draft' || !selectedContract.approval_stage) && can('Subcontract.Submit') && (
                  <button onClick={() => handleApprovalAction(selectedContract, 'approve')}
                    className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                    Submit for Cost Control
                  </button>
                )}
                {selectedContract.approval_stage === 'cost_control' && can('Subcontract.ApproveCostControl') && (
                  <button onClick={() => handleApprovalAction(selectedContract, 'approve')}
                    className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-[#1A7A4A] text-white hover:bg-[#156339]">
                    ✓ Approve
                  </button>
                )}
                {selectedContract.approval_stage === 'director' && can('Subcontract.ApproveDirector') && (
                  <button onClick={() => handleApprovalAction(selectedContract, 'approve')}
                    className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-[#1A7A4A] text-white hover:bg-[#156339]">
                    ✓ Final Approve
                  </button>
                )}
                {['cost_control','director'].includes(selectedContract.approval_stage) && (
                  <button onClick={() => handleApprovalAction(selectedContract, 'reject')}
                    className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                    ✗ Reject
                  </button>
                )}
              </div>
            )}
            {selectedContract.approval_stage === 'rejected' && can('Subcontract.Submit') && (
              <div className="flex items-center gap-3 pt-3 border-t border-[#F3F4F6]">
                <button onClick={() => handleApprovalAction(selectedContract, 'resubmit')}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                  ↻ Resubmit
                </button>
              </div>
            )}
            {selectedContract.approval_stage === 'approved' && (() => {
              const finalApprover = selectedContract.director_approved_by ? 'director' : 'cost_control'
              return (finalApprover === 'director' && can('Subcontract.ApproveDirector')) ||
                (finalApprover === 'cost_control' && can('Subcontract.ApproveCostControl'))
            })() && (
              <div className="flex items-center gap-3 pt-3 border-t border-[#F3F4F6]">
                <button onClick={() => { if(window.confirm('Revoke approval and return this contract to Draft? An Addendum can then be created as a Purchase Order.')) handleApprovalAction(selectedContract, 'revoke') }}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200">
                  ↺ Revoke Approval
                </button>
              </div>
            )}
            {(selectedContract.approval_stage || 'draft') === 'draft' && selectedContract.po_id && can('PO.Submit') && (
              <div className="flex items-center gap-3 pt-3 border-t border-[#F3F4F6]">
                <button onClick={() => { if (onNavigate) onNavigate('purchase-orders') }}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-orange-50 border border-orange-300 text-orange-700 hover:bg-orange-100">
                  + Addendum (in Purchase Orders)
                </button>
              </div>
            )}
          </div>

          {/* Addendum History */}
          <AddendumHistorySection contractId={selectedContract.id} />

          {/* Audit Trail */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-6">
            <h3 className="font-semibold text-[#2E2C29] mb-3">Audit Trail</h3>
            {auditHistory.length === 0 ? (
              <p className="text-xs text-[#9CA3AF]">لا يوجد سجل حتى الآن.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {auditHistory.map(a => (
                  <div key={a.id} className="flex items-start gap-3 text-xs border-b border-[#F3F4F6] pb-2 last:border-0">
                    <span className="text-[#9CA3AF] whitespace-nowrap font-mono">{new Date(a.created_at).toLocaleString('en-GB', {day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
                    <span className="font-semibold text-[#2E2C29]">{a.actor_name || '—'}</span>
                    <span className="text-[#6B7280]">
                      {a.entity_type === 'purchase_order' || a.entity_type === 'rental_equipment_po' ? '[PO] ' : '[Contract] '}
                      {a.action}
                      {a.previous_status && a.new_status ? ` (${a.previous_status} → ${a.new_status})` : ''}
                    </span>
                    {a.comment && <span className="text-[#9CA3AF] italic">— {a.comment}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contract Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'PO Number',       value: selectedContract.purchase_orders?.po_number || '—' },
              { label: 'Contract Value',  value: `SAR ${fmt(selectedContract.contract_value)}` },
              { label: 'Retention',       value: `${selectedContract.retention_pct}%` },
              { label: 'Advance',         value: `${selectedContract.advance_pct}%` },
              { label: 'Adv Recovery Rate', value: `${selectedContract.adv_recovery_rate||0}%` },
              { label: 'VAT Treatment',   value: (selectedContract.vat_case||1)===2 ? 'Case 2 (VAT on Adv)' : 'Case 1 (Standard)' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <p className="text-xs text-[#6B7280] mb-1">{s.label}</p>
                <p className="text-lg font-bold text-[#1C1A18]">{s.value}</p>
              </div>
            ))}
          </div>

          {/* PO Line Items — Read Only */}
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#F3F4F6]">
              <h3 className="font-semibold text-[#2E2C29]">📦 PO Line Items</h3>
              <span className="text-xs text-[#9CA3AF] bg-gray-100 px-2 py-1 rounded">Read Only — التعديل من PO</span>
            </div>
            {poLines.length === 0 ? (
              <div className="py-10 text-center text-[#9CA3AF] text-sm">
                {selectedContract.po_id ? 'لا يوجد بنود لهذا الـ PO' : 'هذا العقد غير مرتبط بـ PO'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F1EA] text-xs text-[#6B7280]">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Ref</th>
                      <th className="text-left px-4 py-2 font-medium">Description</th>
                      <th className="text-left px-4 py-2 font-medium">Cost Code</th>
                      <th className="text-left px-4 py-2 font-medium">Cost Code Description</th>
                      <th className="text-left px-4 py-2 font-medium text-purple-700">Resource Code</th>
                      <th className="text-left px-4 py-2 font-medium text-purple-700">Resource Description</th>
                      <th className="text-left px-4 py-2 font-medium">Sub Code</th>
                      <th className="text-center px-4 py-2 font-medium">Unit</th>
                      <th className="text-right px-4 py-2 font-medium">Qty</th>
                      <th className="text-right px-4 py-2 font-medium">Unit Rate</th>
                      <th className="text-right px-4 py-2 font-medium">Exc. VAT</th>
                      <th className="text-right px-4 py-2 font-medium">Inc. VAT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {poLines.map((l, i) => {
                      const subInfo = subCodesList.find(s => s.code === l.sub_code)
                      return (
                      <tr key={l.id || i} className="hover:bg-[#FAFAFA]">
                        <td className="px-4 py-2 font-mono text-xs text-[#9CA3AF]">{l.ref_code || '—'}</td>
                        <td className="px-4 py-2 text-[#2E2C29]">
                          {l.description}
                          {l.addendum_no && (
                            <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-orange-100 text-orange-700">Addendum #{l.addendum_no}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-[#9CA3AF]">{l.cost_codes?.code || '—'}</td>
                        <td className="px-4 py-2 text-xs text-[#6B7280]">{l.cost_codes?.description || '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs text-purple-700">{subInfo?.resourceCode || '—'}</td>
                        <td className="px-4 py-2 text-xs text-purple-700">{subInfo?.resourceCodeDescription || '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs text-[#1A7A4A]">{l.sub_code || '—'}</td>
                        <td className="px-4 py-2 text-center text-[#6B7280]">{l.unit || '—'}</td>
                        <td className="px-4 py-2 text-right">{fmt(l.qty)}</td>
                        <td className="px-4 py-2 text-right">{fmt(l.unit_rate)}</td>
                        <td className="px-4 py-2 text-right font-medium">{fmt(l.total_exc_vat || l.amount_exc_vat || (parseFloat(l.qty||0) * parseFloat(l.unit_rate||0)))}</td>
                        <td className="px-4 py-2 text-right text-[#6B7280]">{fmt(l.amount_inc_vat)}</td>
                      </tr>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]">
                    <tr>
                      <td colSpan={7} className="px-4 py-2 text-sm font-medium text-[#6B7280]">Total</td>
                      <td className="px-4 py-2 text-right font-bold">{fmt(poLineTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold">{editContract ? 'Edit Contract' : 'New Subcontract'}</h2>
              <button onClick={() => setShowModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-2xl leading-none">×</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">

              {scIsLockedForNonCC && (
                <div className="col-span-2" style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, fontSize:12, color:'#991B1B', fontWeight:600 }}>
                  🔒 This contract has been submitted for approval and cannot be edited now. It will become editable again only if it is Rejected and returns to Draft status.
                </div>
              )}
              {scIsCostControlPartial && (
                <div className="col-span-2" style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, fontSize:12, color:'#1E40AF', fontWeight:600 }}>
                  ℹ️ At this stage, you can only edit the Sub Code and Cost Code. Other fields are locked.
                </div>
              )}

              <fieldset disabled={scIsReadOnly} style={{ display:'contents' }}>

              {/* PO Selector */}
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Link to PO (Approved Only)</label>
                <select value={form.po_id} onChange={e => handlePOSelect(e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100">
                  <option value="">-- بدون PO --</option>
                  {availablePOs.map(po => (
                    <option key={po.id} value={po.id}>
                      {po.po_number} — {po.vendor} — SAR {fmt(po.total_exc_vat)}
                    </option>
                  ))}
                </select>
                {availablePOs.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">⚠ لا يوجد POs بحالة Approved لهذا المشروع</p>
                )}
              </div>

              {/* PO Lines Preview inside modal */}
              {poLines.length > 0 && (
                <div className="col-span-2 bg-[#F5F1EA] rounded-lg border border-[#E5E7EB] overflow-hidden">
                  <p className="text-xs font-medium text-[#6B7280] px-3 py-2 border-b border-[#E5E7EB]">📦 PO Lines ({poLines.length})</p>
                  <div className="overflow-x-auto max-h-36">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left px-3 py-1.5 text-[#6B7280]">Description</th>
                          <th className="text-center px-3 py-1.5 text-[#6B7280]">Unit</th>
                          <th className="text-right px-3 py-1.5 text-[#6B7280]">Qty</th>
                          <th className="text-right px-3 py-1.5 text-[#6B7280]">Rate</th>
                          <th className="text-right px-3 py-1.5 text-[#6B7280]">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F3F4F6]">
                        {poLines.map((l, i) => (
                          <tr key={i} className="bg-white">
                            <td className="px-3 py-1.5 text-[#2E2C29]">{l.description}</td>
                            <td className="px-3 py-1.5 text-center text-[#6B7280]">{l.unit || '—'}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(l.qty)}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(l.unit_rate)}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{fmt(l.total_exc_vat || l.amount_exc_vat || parseFloat(l.qty||0)*parseFloat(l.unit_rate||0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contract No *</label>
                <input value={form.contract_no} onChange={e => hf('contract_no', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>

              {/* Approval stage — read-only, changed only via workflow actions */}
              {editContract ? (
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Approval Stage</label>
                  <div className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium w-full ${APPROVAL_STAGES[editContract.approval_stage||'draft']?.bg} ${APPROVAL_STAGES[editContract.approval_stage||'draft']?.text}`}>
                    {APPROVAL_STAGES[editContract.approval_stage||'draft']?.label}
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Change via approval actions in the detail panel, not here.</p>
                </div>
              ) : <div />}

              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Contractor Name *</label>
                <input value={form.contractor_name} onChange={e => hf('contractor_name', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contact Person</label>
                <input value={form.subcontractor_contact} onChange={e => hf('subcontractor_contact', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Email</label>
                <input value={form.subcontractor_email} onChange={e => hf('subcontractor_email', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Sub Code</label>
                <select value={form.sub_code} onChange={e => {
                    const sub = subCodesList.find(s => s.code === e.target.value)
                    hf('sub_code', e.target.value)
                    hf('cost_code_id', sub?.costCodeId || form.cost_code_id)
                  }}
                  disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100">
                  <option value="">-- Select Sub Code --</option>
                  {subCodesList.map(s => <option key={s.id} value={s.code}>{s.code} — {s.description}</option>)}
                </select>
                {/* Cost Code + Resource Code are derived automatically from the selected Sub Code — never entered manually */}
                {form.sub_code && (() => {
                  const sel = subCodesList.find(s => s.code === form.sub_code)
                  if (!sel) return null
                  return (
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-[#6B7280]">
                        Cost Code: <span className="font-mono font-semibold text-[#1A7A4A]">{sel.costCode}</span> — {sel.costCodeDescription}
                      </p>
                      {sel.resourceCode && (
                        <p className="text-xs text-purple-700">
                          Resource Code: <span className="font-mono font-semibold">{sel.resourceCode}</span> — {sel.resourceCodeDescription}
                        </p>
                      )}
                    </div>
                  )
                })()}
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Scope</label>
                <textarea value={form.scope} onChange={e => hf('scope', e.target.value)} disabled={scIsCostControlPartial}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contract Value (SAR) *</label>
                <input type="number" value={form.contract_value} onChange={e => hf('contract_value', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">VAT %</label>
                <select value={form.vat_pct} onChange={e => hf('vat_pct', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100">
                  {[0, 5, 15].map(v => <option key={v} value={v}>{v}%</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contract Date</label>
                <input type="date" value={form.contract_date} onChange={e => hf('contract_date', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Currency</label>
                <select value={form.currency} onChange={e => hf('currency', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100">
                  {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Retention %</label>
                <input type="number" value={form.retention_pct} onChange={e => hf('retention_pct', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Advance %</label>
                <input type="number" value={form.advance_pct} onChange={e => hf('advance_pct', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Adv Recovery Rate % (per IPC)</label>
                <input type="number" value={form.adv_recovery_rate} onChange={e => hf('adv_recovery_rate', e.target.value)} disabled={scIsCostControlPartial || scFinancialFieldsLocked}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">VAT Treatment</label>
                <select value={form.vat_case} onChange={e => hf('vat_case', e.target.value)} disabled={scIsCostControlPartial || scFinancialFieldsLocked}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100">
                  <option value={1}>Case 1 — Standard (no VAT on advance recovery)</option>
                  <option value={2}>Case 2 — VAT applies on advance recovery</option>
                </select>
                {scFinancialFieldsLocked && (
                  <p className="text-xs text-amber-600 mt-1">🔒 مقفول لوجود مستخلص على هذا العقد. لتعديل هذه القيم، احذف المستخلص أو نفّذ Revoke أولاً.</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Payment Terms (days)</label>
                <input type="number" value={form.payment_terms} onChange={e => hf('payment_terms', e.target.value)} disabled={scIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => hf('notes', e.target.value)} disabled={scIsCostControlPartial}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] disabled:bg-gray-100" />
              </div>

              </fieldset>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#E5E7EB]">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#FAFAFA] text-sm">Cancel</button>
              <button onClick={saveContract} disabled={scIsReadOnly}
                className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg hover:bg-[#156637] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {scIsReadOnly ? '🔒 Locked' : (editContract ? 'Update Contract' : 'Create Contract')}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
      {/* ── Equipment Contract Create/Edit Modal ── */}
      {eqShowModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold">{eqEditContract ? 'Edit Equipment Contract' : 'New Equipment Contract'}</h2>
              <button onClick={() => setEqShowModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-2xl leading-none">×</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">

              {eqScIsLockedForNonCC && (
                <div className="col-span-2" style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, fontSize:12, color:'#991B1B', fontWeight:600 }}>
                  🔒 This contract has been submitted for approval and cannot be edited now. It will become editable again only if it is Rejected and returns to Draft status.
                </div>
              )}
              {eqScIsCostControlPartial && (
                <div className="col-span-2" style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, fontSize:12, color:'#1E40AF', fontWeight:600 }}>
                  ℹ️ At this stage, you can only edit the Sub Code and Cost Code. Other fields are locked.
                </div>
              )}

              <fieldset disabled={eqScIsReadOnly} style={{ display:'contents' }}>

              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Link to Equipment PO (Approved Only)</label>
                <select value={eqForm.po_id} onChange={e => eqHandlePOSelect(e.target.value)} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100">
                  <option value="">-- بدون PO --</option>
                  {eqAvailablePOs.map(po => (
                    <option key={po.id} value={po.id}>
                      {po.po_number} — {po.vendor} — SAR {fmt(po.total_exc_vat)}
                    </option>
                  ))}
                </select>
                {eqAvailablePOs.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">⚠ لا يوجد Equipment POs بحالة Approved لهذا المشروع</p>
                )}
              </div>

              {eqPoLines.length > 0 && (
                <div className="col-span-2 bg-[#F5F1EA] rounded-lg border border-[#E5E7EB] overflow-hidden">
                  <p className="text-xs font-medium text-[#6B7280] px-3 py-2 border-b border-[#E5E7EB]">🚜 PO Lines ({eqPoLines.length})</p>
                  <div className="overflow-x-auto max-h-36">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left px-3 py-1.5 text-[#6B7280]">Equipment Type</th>
                          <th className="text-center px-3 py-1.5 text-[#6B7280]">Unit</th>
                          <th className="text-right px-3 py-1.5 text-[#6B7280]">Qty</th>
                          <th className="text-right px-3 py-1.5 text-[#6B7280]">Rate</th>
                          <th className="text-right px-3 py-1.5 text-[#6B7280]">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F3F4F6]">
                        {eqPoLines.map((l, i) => (
                          <tr key={i} className="bg-white">
                            <td className="px-3 py-1.5 text-[#2E2C29]">{l.equipment_type}</td>
                            <td className="px-3 py-1.5 text-center text-[#6B7280]">{l.unit || '—'}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(l.quantity)}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(l.rate)}</td>
                            <td className="px-3 py-1.5 text-right font-medium">{fmt(l.total_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contract No *</label>
                <input value={eqForm.contract_no} onChange={e => setEqForm(f=>({...f, contract_no: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>

              {eqEditContract ? (
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Approval Stage</label>
                  <div className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium w-full ${APPROVAL_STAGES[eqEditContract.approval_stage||'draft']?.bg} ${APPROVAL_STAGES[eqEditContract.approval_stage||'draft']?.text}`}>
                    {APPROVAL_STAGES[eqEditContract.approval_stage||'draft']?.label}
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Change via approval actions in the detail panel, not here.</p>
                </div>
              ) : <div />}

              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Contractor Name *</label>
                <input value={eqForm.contractor_name} onChange={e => setEqForm(f=>({...f, contractor_name: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contact Person</label>
                <input value={eqForm.subcontractor_contact} onChange={e => setEqForm(f=>({...f, subcontractor_contact: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Email</label>
                <input value={eqForm.subcontractor_email} onChange={e => setEqForm(f=>({...f, subcontractor_email: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Sub Code</label>
                <select value={eqForm.sub_code} onChange={e => {
                    const sub = eqSubCodesList.find(s => s.code === e.target.value)
                    setEqForm(f=>({...f, sub_code: e.target.value, cost_code_id: sub?.costCodeId || f.cost_code_id}))
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="">-- Select Sub Code --</option>
                  {eqSubCodesList.map(s => <option key={s.id} value={s.code}>{s.code} — {s.description}</option>)}
                </select>
                {eqForm.sub_code && (() => {
                  const sel = eqSubCodesList.find(s => s.code === eqForm.sub_code)
                  if (!sel) return null
                  return (
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-[#6B7280]">
                        Cost Code: <span className="font-mono font-semibold text-orange-600">{sel.costCode}</span> — {sel.costCodeDescription}
                      </p>
                      {sel.resourceCode && (
                        <p className="text-xs text-purple-700">
                          Resource Code: <span className="font-mono font-semibold">{sel.resourceCode}</span> — {sel.resourceCodeDescription}
                        </p>
                      )}
                    </div>
                  )
                })()}
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Scope</label>
                <textarea value={eqForm.scope} onChange={e => setEqForm(f=>({...f, scope: e.target.value}))} disabled={eqScIsCostControlPartial}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contract Value (SAR) *</label>
                <input type="number" value={eqForm.contract_value} onChange={e => setEqForm(f=>({...f, contract_value: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">VAT %</label>
                <select value={eqForm.vat_pct} onChange={e => setEqForm(f=>({...f, vat_pct: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100">
                  {[0, 5, 15].map(v => <option key={v} value={v}>{v}%</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Contract Date</label>
                <input type="date" value={eqForm.contract_date} onChange={e => setEqForm(f=>({...f, contract_date: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Currency</label>
                <select value={eqForm.currency} onChange={e => setEqForm(f=>({...f, currency: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100">
                  {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Retention %</label>
                <input type="number" value={eqForm.retention_pct} onChange={e => setEqForm(f=>({...f, retention_pct: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Advance %</label>
                <input type="number" value={eqForm.advance_pct} onChange={e => setEqForm(f=>({...f, advance_pct: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Adv Recovery Rate % (per IPC)</label>
                <input type="number" value={eqForm.adv_recovery_rate} onChange={e => setEqForm(f=>({...f, adv_recovery_rate: e.target.value}))} disabled={eqScIsCostControlPartial || eqScFinancialFieldsLocked}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">VAT Treatment</label>
                <select value={eqForm.vat_case} onChange={e => setEqForm(f=>({...f, vat_case: e.target.value}))} disabled={eqScIsCostControlPartial || eqScFinancialFieldsLocked}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100">
                  <option value={1}>Case 1 — Standard (no VAT on advance recovery)</option>
                  <option value={2}>Case 2 — VAT applies on advance recovery</option>
                </select>
                {eqScFinancialFieldsLocked && (
                  <p className="text-xs text-amber-600 mt-1">🔒 مقفول لوجود مستخلص على هذا العقد. لتعديل هذه القيم، احذف المستخلص أو نفّذ Revoke أولاً.</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Payment Terms (days)</label>
                <input type="number" value={eqForm.payment_terms} onChange={e => setEqForm(f=>({...f, payment_terms: e.target.value}))} disabled={eqScIsCostControlPartial}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B7280] mb-1">Notes</label>
                <textarea value={eqForm.notes} onChange={e => setEqForm(f=>({...f, notes: e.target.value}))} disabled={eqScIsCostControlPartial}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
              </div>

              </fieldset>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#E5E7EB]">
              <button onClick={() => setEqShowModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#FAFAFA] text-sm">Cancel</button>
              <button onClick={eqSaveContract} disabled={eqScIsReadOnly}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {eqScIsReadOnly ? '🔒 Locked' : (eqEditContract ? 'Update Contract' : 'Create Contract')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BULK CONVERT MODAL ── */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <div>
                <h2 className="text-lg font-bold text-[#1C1A18]">🔄 Convert PO Complex → Subcontracts</h2>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  {unconvertedPOs.length === 0
                    ? 'لا يوجد POs جديدة للتحويل'
                    : `${unconvertedPOs.length} PO بحالة Approved غير محوّلة لعقود`}
                </p>
              </div>
              <button onClick={() => { setShowConvertModal(false); setConvertResult(null) }}
                className="text-[#9CA3AF] hover:text-[#6B7280] text-2xl leading-none">×</button>
            </div>

            {/* Result Banner */}
            {convertResult && (
              <div className={`mx-6 mt-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                convertResult.failed === 0 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {convertResult.failed === 0
                  ? `✅ تم تحويل ${convertResult.success} عقد بنجاح!`
                  : `❌ فشل ${convertResult.failed} — نجح ${convertResult.success}`}
                {convertResult.errors?.length > 0 && (
                  <div className="mt-1 text-xs opacity-80 font-normal">{convertResult.errors[0]}</div>
                )}
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {unconvertedPOs.length === 0 ? (
                <div className="text-center py-16 text-[#9CA3AF]">
                  <div className="text-5xl mb-3">✅</div>
                  <p className="font-medium text-[#6B7280]">كل الـ PO Complex محوّلة بالفعل لعقود</p>
                </div>
              ) : convertResult?.success > 0 ? (
                <div className="text-center py-16 text-[#9CA3AF]">
                  <div className="text-5xl mb-3">🎉</div>
                  <p className="font-medium text-[#6B7280]">تمّ التحويل — يمكنك إغلاق هذه النافذة</p>
                </div>
              ) : (
                <>
                  {/* Select All */}
                  <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#F3F4F6]">
                    <input
                      type="checkbox"
                      id="select-all-convert"
                      checked={convertRows.every(r => r.checked)}
                      onChange={e => setConvertRows(prev => prev.map(r => ({ ...r, checked: e.target.checked })))}
                      className="w-4 h-4 rounded border-gray-300 text-[#1A7A4A] cursor-pointer"
                    />
                    <label htmlFor="select-all-convert" className="text-xs text-[#6B7280] cursor-pointer select-none">
                      تحديد الكل ({convertRows.filter(r => r.checked).length} / {convertRows.length})
                    </label>
                  </div>

                  {/* PO Rows */}
                  <div className="space-y-2">
                    {convertRows.map((row, idx) => (
                      <div key={row.po.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          row.checked ? 'bg-[#EDE7DA] border-indigo-200' : 'bg-[#F5F1EA] border-[#E5E7EB] opacity-60'
                        }`}>
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={row.checked}
                          onChange={() => toggleConvertRow(idx)}
                          className="w-4 h-4 rounded border-gray-300 text-[#1A7A4A] cursor-pointer flex-shrink-0"
                        />
                        {/* PO Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-mono text-xs font-semibold text-[#1A7A4A] bg-[#DDD9D0] px-2 py-0.5 rounded">
                              {row.po.po_number}
                            </span>
                            <span className="text-sm font-medium text-[#2E2C29] truncate">{row.po.vendor}</span>
                          </div>
                          <div className="text-xs text-[#9CA3AF]">
                            SAR {fmt(row.po.total_exc_vat)} exc. VAT
                            {row.po.notes && <span className="ml-2 truncate max-w-xs inline-block align-bottom">{row.po.notes}</span>}
                          </div>
                        </div>
                        {/* Auto Contract No — editable */}
                        <div className="flex-shrink-0 flex items-center gap-1.5">
                          <span className="text-xs text-[#9CA3AF]">SC No:</span>
                          <input
                            value={row.contract_no}
                            onChange={e => updateConvertNo(idx, e.target.value)}
                            disabled={!row.checked}
                            className="w-28 border border-gray-300 rounded-lg px-2 py-1 text-xs font-mono focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                        {/* Retention % */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-xs text-[#9CA3AF]">Ret%:</span>
                          <input
                            type="number" min="0" max="100"
                            value={row.retention_pct}
                            onChange={e => updateConvertField(idx, 'retention_pct', e.target.value)}
                            disabled={!row.checked}
                            className="w-14 border border-gray-300 rounded-lg px-2 py-1 text-xs text-center focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                        {/* Payment Terms */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-xs text-[#9CA3AF]">Pay:</span>
                          <input
                            type="number" min="0"
                            value={row.payment_terms}
                            onChange={e => updateConvertField(idx, 'payment_terms', e.target.value)}
                            disabled={!row.checked}
                            className="w-14 border border-gray-300 rounded-lg px-2 py-1 text-xs text-center focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Info note */}
                  <p className="text-xs text-[#9CA3AF] mt-4">
                    💡 العقود ستُنشأ بحالة <strong>Draft</strong> — عدّل Ret% و Pay (days) لكل عقد قبل التحويل
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            {unconvertedPOs.length > 0 && !convertResult?.success && (
              <div className="flex gap-3 px-6 py-4 border-t border-[#E5E7EB] bg-[#F5F1EA] rounded-b-2xl">
                <button
                  onClick={() => { setShowConvertModal(false); setConvertResult(null) }}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#EDE7DA] text-xs text-[#6B7280]">
                  إلغاء
                </button>
                <button
                  onClick={confirmBulkConvert}
                  disabled={converting || convertRows.every(r => !r.checked)}
                  className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg hover:bg-[#2E2C29] text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {converting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      جاري التحويل...
                    </>
                  ) : (
                    `🔄 تحويل ${convertRows.filter(r => r.checked).length} عقد`
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── EQUIPMENT BULK CONVERT MODAL ── */}
      {eqShowConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <div>
                <h2 className="text-lg font-bold text-[#1C1A18]">🔄 Convert Equipment PO → Contracts</h2>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  {eqUnconvertedPOs.length === 0
                    ? 'لا يوجد Equipment POs جديدة للتحويل'
                    : `${eqUnconvertedPOs.length} PO بحالة Approved غير محوّلة لعقود`}
                </p>
              </div>
              <button onClick={() => { setEqShowConvertModal(false); setEqConvertResult(null) }}
                className="text-[#9CA3AF] hover:text-[#6B7280] text-2xl leading-none">×</button>
            </div>

            {/* Result Banner */}
            {eqConvertResult && (
              <div className={`mx-6 mt-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                eqConvertResult.failed === 0 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {eqConvertResult.failed === 0
                  ? `✅ تم تحويل ${eqConvertResult.success} عقد بنجاح!`
                  : `❌ فشل ${eqConvertResult.failed} — نجح ${eqConvertResult.success}`}
                {eqConvertResult.errors?.length > 0 && (
                  <div className="mt-1 text-xs opacity-80 font-normal">{eqConvertResult.errors[0]}</div>
                )}
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {eqUnconvertedPOs.length === 0 ? (
                <div className="text-center py-16 text-[#9CA3AF]">
                  <div className="text-5xl mb-3">✅</div>
                  <p className="font-medium text-[#6B7280]">كل الـ Equipment POs محوّلة بالفعل لعقود</p>
                </div>
              ) : eqConvertResult?.success > 0 ? (
                <div className="text-center py-16 text-[#9CA3AF]">
                  <div className="text-5xl mb-3">🎉</div>
                  <p className="font-medium text-[#6B7280]">تمّ التحويل — يمكنك إغلاق هذه النافذة</p>
                </div>
              ) : (
                <>
                  {/* Select All */}
                  <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#F3F4F6]">
                    <input
                      type="checkbox"
                      id="select-all-eq-convert"
                      checked={eqConvertRows.every(r => r.checked)}
                      onChange={e => setEqConvertRows(prev => prev.map(r => ({ ...r, checked: e.target.checked })))}
                      className="w-4 h-4 rounded border-gray-300 text-orange-500 cursor-pointer"
                    />
                    <label htmlFor="select-all-eq-convert" className="text-xs text-[#6B7280] cursor-pointer select-none">
                      تحديد الكل ({eqConvertRows.filter(r => r.checked).length} / {eqConvertRows.length})
                    </label>
                  </div>

                  {/* PO Rows */}
                  <div className="space-y-2">
                    {eqConvertRows.map((row, idx) => (
                      <div key={row.po.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          row.checked ? 'bg-[#EDE7DA] border-orange-200' : 'bg-[#F5F1EA] border-[#E5E7EB] opacity-60'
                        }`}>
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={row.checked}
                          onChange={() => eqToggleConvertRow(idx)}
                          className="w-4 h-4 rounded border-gray-300 text-orange-500 cursor-pointer flex-shrink-0"
                        />
                        {/* PO Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-mono text-xs font-semibold text-orange-600 bg-[#DDD9D0] px-2 py-0.5 rounded">
                              {row.po.po_number}
                            </span>
                            <span className="text-sm font-medium text-[#2E2C29] truncate">{row.po.vendor}</span>
                          </div>
                          <div className="text-xs text-[#9CA3AF]">
                            SAR {fmt(row.po.total_exc_vat)} exc. VAT
                            {row.po.notes && <span className="ml-2 truncate max-w-xs inline-block align-bottom">{row.po.notes}</span>}
                          </div>
                        </div>
                        {/* Auto Contract No — editable */}
                        <div className="flex-shrink-0 flex items-center gap-1.5">
                          <span className="text-xs text-[#9CA3AF]">EC No:</span>
                          <input
                            value={row.contract_no}
                            onChange={e => eqUpdateConvertNo(idx, e.target.value)}
                            disabled={!row.checked}
                            className="w-28 border border-gray-300 rounded-lg px-2 py-1 text-xs font-mono focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                        {/* Retention % */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-xs text-[#9CA3AF]">Ret%:</span>
                          <input
                            type="number" min="0" max="100"
                            value={row.retention_pct}
                            onChange={e => eqUpdateConvertField(idx, 'retention_pct', e.target.value)}
                            disabled={!row.checked}
                            className="w-14 border border-gray-300 rounded-lg px-2 py-1 text-xs text-center focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                        {/* Advance % */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-xs text-[#9CA3AF]">Adv%:</span>
                          <input
                            type="number" min="0" max="100"
                            value={row.advance_pct}
                            onChange={e => eqUpdateConvertField(idx, 'advance_pct', e.target.value)}
                            disabled={!row.checked}
                            className="w-14 border border-gray-300 rounded-lg px-2 py-1 text-xs text-center focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                        {/* Payment Terms */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-xs text-[#9CA3AF]">Pay:</span>
                          <input
                            type="number" min="0"
                            value={row.payment_terms}
                            onChange={e => eqUpdateConvertField(idx, 'payment_terms', e.target.value)}
                            disabled={!row.checked}
                            className="w-14 border border-gray-300 rounded-lg px-2 py-1 text-xs text-center focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Info note */}
                  <p className="text-xs text-[#9CA3AF] mt-4">
                    💡 العقود ستُنشأ بحالة <strong>Draft</strong> — عدّل Ret%, Adv% و Pay (days) لكل عقد قبل التحويل (افتراضيًا 0%)
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            {eqUnconvertedPOs.length > 0 && !eqConvertResult?.success && (
              <div className="flex gap-3 px-6 py-4 border-t border-[#E5E7EB] bg-[#F5F1EA] rounded-b-2xl">
                <button
                  onClick={() => { setEqShowConvertModal(false); setEqConvertResult(null) }}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#EDE7DA] text-xs text-[#6B7280]">
                  إلغاء
                </button>
                <button
                  onClick={eqConfirmBulkConvert}
                  disabled={eqConverting || eqConvertRows.every(r => !r.checked)}
                  className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {eqConverting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      جاري التحويل...
                    </>
                  ) : (
                    `🔄 تحويل ${eqConvertRows.filter(r => r.checked).length} عقد`
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    {showSubReport && (
      <SubcontractReportControls projectId={projectId} onClose={() => setShowSubReport(false)} />
    )}
    {printContractId && (
      <ContractDocument subcontractId={printContractId} onClose={() => setPrintContractId(null)} />
    )}
    {eqPrintContractId && (
      <ContractDocument subcontractId={eqPrintContractId} docType="equipment" onClose={() => setEqPrintContractId(null)} />
    )}
    {rejectTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={() => setRejectTarget(null)}/>
        <div className="relative bg-white rounded-xl shadow-2xl p-6 w-96">
          <h3 className="font-semibold text-[#1C1A18] mb-1">Reject Contract</h3>
          <p className="text-xs text-[#6B7280] mb-4">{rejectTarget.contract_no} — {rejectTarget.contractor_name}</p>
          <label className="block text-xs text-[#6B7280] mb-1">Reason (optional)</label>
          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:ring-2 focus:ring-red-400"
            placeholder="Why is this contract being rejected?"/>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setRejectTarget(null)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
            <button onClick={confirmReject} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Confirm Reject</button>
          </div>
        </div>
      </div>
    )}
    {eqRejectTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={() => setEqRejectTarget(null)}/>
        <div className="relative bg-white rounded-xl shadow-2xl p-6 w-96">
          <h3 className="font-semibold text-[#1C1A18] mb-1">Reject Equipment Contract</h3>
          <p className="text-xs text-[#6B7280] mb-4">{eqRejectTarget.contract_no} — {eqRejectTarget.contractor_name}</p>
          <label className="block text-xs text-[#6B7280] mb-1">Reason (optional)</label>
          <textarea value={eqRejectReason} onChange={e => setEqRejectReason(e.target.value)} rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:ring-2 focus:ring-red-400"
            placeholder="Why is this contract being rejected?"/>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEqRejectTarget(null)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
            <button onClick={eqConfirmReject} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Confirm Reject</button>
          </div>
        </div>
      </div>
    )}
    {/* Addendum creation modal REMOVED — Addendums are now created as
        full Purchase Orders in PurchaseOrders.jsx, not via a form on
        this page (see openAddendumPO()/repOpenAddendumPO() there). */}

    {/* ── Statement of Account Modal ── */}
    {statementContract && (
      <div style={{ position:'fixed', inset:0, zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div onClick={() => { setStatementContract(null); setStatementRows([]) }} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.45)', backdropFilter:'blur(3px)' }}/>
        <div style={{ position:'relative', zIndex:1, width:900, maxWidth:'92vw', maxHeight:'85vh', background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', boxShadow:'0 24px 64px rgba(0,0,0,.15)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div>
              <h2 style={{ fontSize:15, fontWeight:800, color:'#0F1017' }}>
                {statementContract.contract_type==='equipment'?'🚜':'📦'} Statement of Account — {statementContract.contract_no}
              </h2>
              <p style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>{statementContract.contractor_name}</p>
            </div>
            <button onClick={() => { setStatementContract(null); setStatementRows([]) }}
              style={{ width:28, height:28, borderRadius:6, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:20 }}>
            {statementLoading ? (
              <div style={{padding:60,textAlign:'center',color:'#9CA3AF'}}>Loading…</div>
            ) : !statementRows.rows || statementRows.rows.length === 0 ? (
              <div style={{padding:60,textAlign:'center',color:'#9CA3AF'}}>لا يوجد مستخلصات معتمدة على هذا العقد بعد.</div>
            ) : (
              <>
                {statementRows.unlinkedTotal > 0 && (
                  <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#92400E'}}>
                    ⚠ يوجد دفعات بقيمة SAR {fmt(statementRows.unlinkedTotal)} على هذا العقد غير مرتبطة بمستخلص محدد بعد (دفعات قديمة). قيم "المدفوع" و"الرصيد" تحت تعتمد فقط على الدفعات المرتبطة فعليًا بمستخلص. اربطها من شاشة Payment Entries للحصول على كشف حساب دقيق بالكامل.
                  </div>
                )}
                <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',fontSize:12.5,borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'#F9FAFB',borderBottom:'1px solid #E5E7EB'}}>
                      <th style={{textAlign:'left',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>IPC</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280'}}>Gross</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#DC2626'}}>Retention</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1A7A4A'}}>Net Payable</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1A7A4A'}}>Cumulative Net</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1D4ED8'}}>Paid (this IPC)</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#1D4ED8'}}>Cumulative Paid</th>
                      <th style={{textAlign:'right',padding:'8px 12px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#0F1017'}}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statementRows.rows.map((r, idx) => (
                      <tr key={idx} style={{borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'8px 12px',fontFamily:'monospace'}}>IPC-{String(r.ipc_number).padStart(4,'0')}</td>
                        <td style={{padding:'8px 12px',textAlign:'right'}}>{fmt(r.gross)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',color:'#DC2626'}}>{fmt(r.retention)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',fontWeight:600}}>{fmt(r.net_payable)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700}}>{fmt(r.cumulative_net)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',color:'#1D4ED8'}}>{fmt(r.paid_this_ipc)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',color:'#1D4ED8',fontWeight:600}}>{fmt(r.cumulative_paid_linked)}</td>
                        <td style={{padding:'8px 12px',textAlign:'right',fontWeight:700,color:r.balance_linked>0?'#DC2626':r.balance_linked<0?'#D97706':'#1A7A4A'}}>
                          {r.balance_linked>0 ? fmt(r.balance_linked) : r.balance_linked<0 ? `(${fmt(Math.abs(r.balance_linked))})` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  )
}

/**
 * AddendumHistorySection — the permanent Addendum History record for one
 * contract. Reads subcontract_addendums directly — the single durable
 * source for this data. Per the fully-revised workflow, every row here
 * now corresponds to a fully-approved Addendum PO (written by
 * PurchaseOrders.jsx's handlePOApproval()/repHandleApproval() at the
 * moment that PO reaches 'approved' — see addendum_po_id below), not a
 * manually-entered form record as in the prior, now-removed,
 * Subcontracts.jsx-based Addendum creation flow.
 *
 * addendum_po_id (new column, this revision) links each row back to the
 * specific Addendum PO that created it — the first genuinely durable,
 * explicit link in either direction (Contract ↔ Addendum ↔ Addendum PO
 * ↔ Original PO), replacing the prior, purely-temporary
 * pending_addendum_no mechanism this revision retires.
 */
function AddendumHistorySection({ contractId }) {
  const [addendums, setAddendums] = useState(null) // null = loading

  useEffect(() => {
    if (!contractId) return
    let cancelled = false
    async function load() {
      const { data } = await supabase
        .from('subcontract_addendums')
        .select('addendum_no, contract_type, description, value_change, new_contract_value, addendum_date, created_by, created_at, addendum_po_id')
        .eq('contract_id', contractId)
        .order('addendum_no', { ascending: true })
      if (!cancelled) setAddendums(data || [])
    }
    load()
    return () => { cancelled = true }
  }, [contractId])

  if (addendums === null) {
    return <div style={{ fontSize: 11.5, color: '#9CA3AF', padding: '12px 0' }}>Loading addendum history…</div>
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-6">
      <div className="text-sm font-semibold text-[#1C1A18] mb-3">Addendum History</div>
      {addendums.length === 0 ? (
        <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>No addendums recorded for this contract.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['#', 'Type', 'Reason / Description', 'Amount', 'Date', 'Created By', 'Addendum PO'].map(h => (
                <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Amount' ? 'right' : 'left', fontSize: 10, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {addendums.map(a => (
              <tr key={a.addendum_no} style={{ borderTop: '1px solid #F3F4F6' }}>
                <td style={{ padding: '6px 8px', fontWeight: 700, color: '#C2410C' }}>#{a.addendum_no}</td>
                <td style={{ padding: '6px 8px', color: '#6B7280' }}>{a.contract_type === 'equipment' ? 'Equipment' : 'Material'}</td>
                <td style={{ padding: '6px 8px' }}>{a.description || '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: (a.value_change || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                  {(a.value_change || 0) >= 0 ? '+' : ''}{fmt(a.value_change)}
                </td>
                <td style={{ padding: '6px 8px', color: '#6B7280' }}>{a.addendum_date || '—'}</td>
                <td style={{ padding: '6px 8px', color: '#6B7280' }}>{a.created_by || '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  {a.addendum_po_id ? (
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 9.5, fontWeight: 700, background: '#F0FDF4', color: '#166534' }}>Approved PO</span>
                  ) : (
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 9.5, fontWeight: 700, background: '#F3F4F6', color: '#6B7280' }}>Legacy record</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
